import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { safeFormatTimestamp } from '../lib/accountFormatters';
import {
  Play,
  Zap,
  Plus,
  Search,
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  Activity,
  Calendar,
  Layers,
  Target,
  ShieldCheck,
  Settings,
  ChevronRight,
  Clock,
  Info,
  Maximize2,
  FastForward,
  Rewind,
  Terminal,
  Cpu,
  History,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  LineChart as LineChartIcon,
  BookOpen,
  Trophy,
  Users,
  CheckCircle2,
  X,
  ArrowRight,
  PlayCircle,
  PauseCircle,
  Loader2,
  RefreshCw,
  XCircle,
  Ban,
  Lock,
  Shield,
  Brain,
  ChevronDown,
  ChevronUp,
  Eye,
} from 'lucide-react';
import { OwlIcon, ElephantIcon, BrainIcon, CompassIcon } from './AIIcons';
import { motion, AnimatePresence } from 'motion/react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { cn, logInteraction } from '../lib/utils';
import { ErrorBoundary } from './ErrorBoundary';

// Per-sim-account branded icon for IDs like sim-brain-*, sim-owl-*, etc.
// Returns null when the id does not contain a recognized animal keyword,
// so the existing UI is unchanged for non-sim accounts.
function SimAccountIcon({ id, size = 14 }: { id: string | undefined | null; size?: number }) {
  if (!id) return null;
  const s = String(id).toLowerCase();
  if (s.includes('brain'))    return <BrainIcon size={size} />;
  if (s.includes('owl'))      return <OwlIcon size={size} />;
  if (s.includes('elephant')) return <ElephantIcon size={size} />;
  if (s.includes('compass'))  return <CompassIcon size={size} />;
  return null;
}
import { useFeedback } from './FeedbackContext';
import {
  MOCK_TRADE_LOGS,
  SimulationAccount,
  TradeLog
} from '../services/simulationService';
import { useSimulationSharedAccounts } from '../hooks/useSimulationSharedAccounts';
import { useSimulationStore } from '../hooks/useSimulationStore';
import { cancelActiveSimulation } from '../services/simulationAdapter';
import { useAccountLiveData } from '../hooks/useAccountLiveData';
import type { BrainDecision } from '../hooks/useAccountLiveData';
import ForexPanel from './ForexPanel';
import AccountSupervisionPanel from './AccountSupervisionPanel';
import { useForexPaperAccount } from '../hooks/useForexPaperAccount';
import type {
  SimulationResult,
  SimulationMetrics,
  SimulationPosition,
  TimelineEntry,
  StressScenario,
} from '../types/simulation';

// ── Utility: Eastern Time formatter ──
function formatET(isoStr: string | null | undefined): { time: string; date: string; full: string } {
  if (!isoStr) return { time: '\u2014', date: '', full: '\u2014' };
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return { time: '\u2014', date: '', full: '\u2014' };
    const time = d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true }) + ' ET';
    const date = d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' });
    return { time, date, full: `${date} ${time}` };
  } catch { return { time: '\u2014', date: '', full: '\u2014' }; }
}

// ── Utility: Strip markdown fragments ──
function cleanMarkdown(text: string | null | undefined): string {
  if (!text) return '';
  return text.replace(/^#{1,6}\s*/gm, '').replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1').replace(/`(.*?)`/g, '$1').trim();
}

// ── Activation dates for curve validation ──
const ACCOUNT_ACTIVATION_DATES: Record<string, string> = {
  'sim-compass-swing': '2026-03-13',
  'sim-owl-momentum': '2026-04-16',
  'sim-brain-scalper': '2026-04-16',
};

// ── Loading skeleton for panels during account switch ──
function PanelSkeleton({ rows = 3, label = '' }: { rows?: number; label?: string }) {
  return (
    <div className="animate-pulse space-y-3 py-4">
      {label && <div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-widest">{label}</div>}
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-3">
          <div className="h-3 bg-[var(--card-border)] rounded w-1/4" />
          <div className="h-3 bg-[var(--card-border)] rounded w-1/3" />
          <div className="h-3 bg-[var(--card-border)] rounded w-1/6" />
        </div>
      ))}
    </div>
  );
}

// ── Source label badge ──
function SourceLabel({ source, icon }: { source: string; icon?: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/8 border border-emerald-500/15 rounded-lg text-[7px] font-black text-emerald-600 uppercase tracking-widest">
      {icon || <Shield size={8} />} Source: {source}
    </span>
  );
}

type SimulationTab = 'live-accounts' | 'trade-replay' | 'stress-testing';

// ─── Null-safety helpers (added 2026-05-16) ───────────────────────────────
// Used by the Simulation Lab to safely render and compare metric values that
// the backend models as `number | null` (e.g. winRate, roi, tradeCount).
//
// `null` is NEVER equated to 0 for display. Sort comparators push null to
// the BOTTOM of the ranking instead of treating it as 0%.
function safeMetricValue<T>(v: T | null | undefined, fallback: T): T {
  return v === null || v === undefined ? fallback : v;
}
function formatNullablePercent(v: number | null | undefined, digits: number = 1): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '\u2014';
  return v.toFixed(digits) + '%';
}
function formatNullableNumber(v: number | null | undefined, digits: number = 0): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '\u2014';
  return digits > 0 ? v.toFixed(digits) : v.toLocaleString();
}
function sortKeyOrPushDown(v: number | null | undefined): number {
  // Pushes accounts with no metric value to the BOTTOM of a descending sort.
  return v === null || v === undefined || Number.isNaN(v) ? Number.NEGATIVE_INFINITY : v;
}
// ──────────────────────────────────────────────────────────────────────────


// ═══════════════════════════════════════════════════════════════════════
//  DATA FRESHNESS — "Live" / "Updated X min ago" / "Stale"
// ═══════════════════════════════════════════════════════════════════════

function getDataFreshness(lastSyncedAt: string | null): { label: string; color: string; dotColor: string } {
  if (!lastSyncedAt) return { label: 'No sync', color: 'text-[var(--muted-text)]', dotColor: 'bg-[var(--muted-text)]' };
  const diffMs = Date.now() - new Date(lastSyncedAt).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 2) return { label: 'Live', color: 'text-emerald-500', dotColor: 'bg-emerald-500' };
  if (diffMin <= 10) return { label: `Updated ${diffMin}m ago`, color: 'text-amber-500', dotColor: 'bg-amber-500' };
  return { label: 'Stale', color: 'text-rose-500', dotColor: 'bg-rose-500' };
}

// ═══════════════════════════════════════════════════════════════════════
//  STATUS BANNER — Shows store state across all tabs
// ═══════════════════════════════════════════════════════════════════════

function SimulationStatusBanner() {
  const sim = useSimulationStore();

  if (sim.isIdle) return null;

  return (
    <div data-testid="sim-status-banner" className="w-full">
      <AnimatePresence mode="wait">
        {/* Loading / Running */}
        {(sim.isLoading || sim.isRunning) && (
          <motion.div
            key="loading"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center justify-between p-4 bg-sky-500/10 border border-sky-500/20 rounded-2xl"
          >
            <div className="flex items-center gap-3">
              <Loader2 size={18} className="text-sky-600 animate-spin" />
              <div>
                <div className="text-[10px] font-black text-sky-600 uppercase italic">
                  {sim.isLoading ? 'Starting Simulation...' : `Running — ${Math.round(sim.progress)}%`}
                </div>
                {sim.simulationId && (
                  <div className="text-[8px] font-bold text-[var(--muted-text)] uppercase tracking-widest">ID: {sim.simulationId}</div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {sim.progress > 0 && (
                <div className="w-32 h-1.5 bg-sky-500/20 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-sky-500 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${sim.progress}%` }}
                  />
                </div>
              )}
              <button
                data-testid="sim-cancel-btn"
                onClick={() => sim.cancel()}
                className="p-1.5 hover:bg-sky-500/20 rounded-lg transition-all text-sky-600"
                title="Cancel simulation"
              >
                <X size={14} />
              </button>
            </div>
          </motion.div>
        )}

        {/* Retrying */}
        {sim.retrying && (
          <motion.div
            key="retrying"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            data-testid="sim-retry-banner"
            className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl"
          >
            <RefreshCw size={18} className="text-amber-600 animate-spin" />
            <div>
              <div className="text-[10px] font-black text-amber-600 uppercase italic">Retrying... Attempt {sim.retryCount}</div>
              <div className="text-[8px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Automatic retry in progress</div>
            </div>
          </motion.div>
        )}

        {/* Partial */}
        {sim.isPartial && (
          <motion.div
            key="partial"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            data-testid="sim-partial-banner"
            className="flex items-center gap-3 p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-2xl"
          >
            <Activity size={18} className="text-indigo-600" />
            <div>
              <div className="text-[10px] font-black text-indigo-600 uppercase italic">Partial Results Available</div>
              <div className="text-[8px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Simulation still processing — rendering available data</div>
            </div>
          </motion.div>
        )}

        {/* Success */}
        {sim.isSuccess && (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            data-testid="sim-success-banner"
            className="flex items-center justify-between p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl"
          >
            <div className="flex items-center gap-3">
              <CheckCircle2 size={18} className="text-emerald-600" />
              <div className="text-[10px] font-black text-emerald-600 uppercase italic">Simulation Complete</div>
            </div>
            <button
              onClick={() => sim.reset()}
              className="px-3 py-1 text-[9px] font-black text-emerald-600 uppercase italic border border-emerald-500/20 rounded-lg hover:bg-emerald-500/10 transition-all"
            >
              Clear
            </button>
          </motion.div>
        )}

        {/* Error */}
        {sim.isError && (
          <motion.div
            key="error"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            data-testid="sim-error-banner"
            className="flex items-center justify-between p-4 bg-rose-500/10 border border-rose-500/20 rounded-2xl"
          >
            <div className="flex items-center gap-3">
              <XCircle size={18} className="text-rose-600" />
              <div>
                <div className="text-[10px] font-black text-rose-600 uppercase italic">
                  {sim.error?.userMessage || 'Data unavailable. Please retry.'}
                </div>
                {sim.errorType && (
                  <div className="text-[8px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Type: {sim.errorType}</div>
                )}
              </div>
            </div>
            <button
              data-testid="sim-error-dismiss"
              onClick={() => sim.reset()}
              className="px-3 py-1 text-[9px] font-black text-rose-600 uppercase italic border border-rose-500/20 rounded-lg hover:bg-rose-500/10 transition-all"
            >
              Dismiss
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  SIMULATION RESULT PANEL — Renders normalized result contract
// ═══════════════════════════════════════════════════════════════════════

function SimulationResultPanel({ result, label }: { result: SimulationResult | null; label?: string }) {
  if (!result) return null;

  const metrics = result.metrics;
  const summary = result.summary;
  const timeline = result.timeline ?? [];
  const positions = result.positions ?? [];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      data-testid="sim-result-panel"
      className="w-full space-y-8"
    >
      {/* Summary header */}
      <div className="space-y-2">
        <div className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.2em]">
          {label || 'Simulation Results'}
        </div>
        <h4 className="text-3xl font-black text-[var(--app-text)] uppercase italic">
          {summary?.strategy || 'Simulation'}
        </h4>
        <p className="text-xs text-[var(--muted-text)] font-medium italic">
          {summary?.description || ''}
        </p>
        {(summary?.tags ?? []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {summary.tags.map((tag, i) => (
              <span key={i} className="px-2 py-0.5 bg-emerald-500/10 text-emerald-600 text-[8px] font-black uppercase rounded-lg">{tag}</span>
            ))}
          </div>
        )}
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-5 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-2xl space-y-1">
          <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Total Return</div>
          <div className={cn("text-2xl font-black italic", (metrics?.totalReturnPct ?? 0) >= 0 ? "text-emerald-600" : "text-rose-600")}>
            {(metrics?.totalReturnPct ?? 0) >= 0 ? '+' : ''}{(metrics?.totalReturnPct ?? 0).toFixed(2)}%
          </div>
        </div>
        <div className="p-5 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-2xl space-y-1">
          <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Max Drawdown</div>
          <div className="text-2xl font-black text-rose-600 italic">-{(metrics?.maxDrawdownPct ?? 0).toFixed(2)}%</div>
        </div>
        <div className="p-5 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-2xl space-y-1">
          <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Win Rate</div>
          <div className="text-2xl font-black text-[var(--app-text)] italic">{(metrics?.winRate ?? 0).toFixed(1)}%</div>
        </div>
        <div className="p-5 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-2xl space-y-1">
          <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Sharpe Ratio</div>
          <div className="text-2xl font-black text-[var(--app-text)] italic">{(metrics?.sharpeRatio ?? 0).toFixed(2)}</div>
        </div>
      </div>

      {/* Extended Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { label: 'Starting Capital', value: `$${(summary?.startingCapital ?? 0).toLocaleString()}` },
          { label: 'Final Capital', value: `$${(summary?.finalCapital ?? 0).toLocaleString()}` },
          { label: 'Total Trades', value: String(metrics?.totalTrades ?? 0) },
          { label: 'Winning', value: String(metrics?.winningTrades ?? 0) },
          { label: 'Losing', value: String(metrics?.losingTrades ?? 0) },
          { label: 'Profit Factor', value: (metrics?.profitFactor ?? 0).toFixed(2) },
          { label: 'Sortino', value: (metrics?.sortinoRatio ?? 0).toFixed(2) },
          { label: 'Calmar', value: (metrics?.calmarRatio ?? 0).toFixed(2) },
          { label: 'Volatility', value: `${(metrics?.volatility ?? 0).toFixed(1)}%` },
          { label: 'Avg Win', value: `$${(metrics?.avgWin ?? 0).toFixed(0)}` },
          { label: 'Avg Loss', value: `$${(metrics?.avgLoss ?? 0).toFixed(0)}` },
          { label: 'Avg Hold', value: metrics?.avgHoldingPeriod ?? 'N/A' },
        ].map((m) => (
          <div key={m.label} className="p-3 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-xl">
            <div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-tighter">{m.label}</div>
            <div className="text-[11px] font-black text-[var(--app-text)] italic">{m.value}</div>
          </div>
        ))}
      </div>

      {/* Risk & Confidence */}
      <div className="flex items-center gap-4">
        {summary?.riskLevel && (
          <span className={cn(
            "px-3 py-1 rounded-lg text-[9px] font-black uppercase italic",
            summary.riskLevel === 'Extreme' ? 'bg-rose-500/10 text-rose-600' :
            summary.riskLevel === 'High' ? 'bg-amber-500/10 text-amber-600' :
            summary.riskLevel === 'Medium' ? 'bg-sky-500/10 text-sky-600' :
            'bg-emerald-500/10 text-emerald-600'
          )}>
            Risk: {summary.riskLevel}
          </span>
        )}
        {summary?.aiConfidence != null && (
          <span className="px-3 py-1 rounded-lg text-[9px] font-black uppercase italic bg-indigo-500/10 text-indigo-600">
            AI Confidence: {summary.aiConfidence.toFixed(0)}%
          </span>
        )}
      </div>

      {/* Equity Curve */}
      {timeline.length > 0 && (
        <div className="bg-[var(--input-bg)] rounded-2xl p-6 border border-[var(--input-border)]">
          <h5 className="text-xs font-black text-[var(--app-text)] uppercase italic mb-4 flex items-center gap-2">
            <LineChartIcon size={16} className="text-emerald-600" /> Equity Curve
          </h5>
          <div className="h-[200px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeline}>
                <defs>
                  <linearGradient id="resultEquityGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--card-border)" vertical={false} />
                <XAxis dataKey="timestamp" hide />
                <YAxis hide domain={['auto', 'auto']} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '12px', fontSize: '10px' }} />
                <Area type="monotone" dataKey="equity" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#resultEquityGrad)" />
                {timeline[0]?.benchmark != null && (
                  <Line type="monotone" dataKey="benchmark" stroke="#6366f1" strokeWidth={1.5} strokeDasharray="5 5" dot={false} />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Positions Table */}
      {positions.length > 0 && (
        <div className="bg-[var(--input-bg)] rounded-2xl border border-[var(--input-border)] overflow-hidden">
          <div className="px-6 py-4 border-b border-[var(--card-border)]">
            <h5 className="text-xs font-black text-[var(--app-text)] uppercase italic flex items-center gap-2">
              <Target size={16} className="text-sky-500" /> Positions ({positions.length})
            </h5>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-widest bg-[var(--card-bg)]">
                  <th className="px-6 py-3">Symbol</th>
                  <th className="px-6 py-3">Side</th>
                  <th className="px-6 py-3">Qty</th>
                  <th className="px-6 py-3">Entry</th>
                  <th className="px-6 py-3">Current</th>
                  <th className="px-6 py-3">P&L</th>
                  <th className="px-6 py-3">Weight</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--card-border)]">
                {positions.map((pos, i) => (
                  <tr key={`${pos.symbol}-${i}`} className="hover:bg-[var(--card-bg)] transition-colors">
                    <td className="px-6 py-3 text-[10px] font-black text-[var(--app-text)] italic">{pos.symbol}</td>
                    <td className="px-6 py-3">
                      <span className={cn("px-2 py-0.5 rounded text-[9px] font-black uppercase", pos.side === 'BUY' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-rose-500/10 text-rose-600')}>
                        {pos.side}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-[10px] font-bold text-[var(--muted-text)]">{pos.quantity}</td>
                    <td className="px-6 py-3 text-[10px] font-bold text-[var(--muted-text)]">${pos.entryPrice?.toFixed(2)}</td>
                    <td className="px-6 py-3 text-[10px] font-bold text-[var(--muted-text)]">${pos.currentPrice?.toFixed(2)}</td>
                    <td className="px-6 py-3">
                      <span className={cn("text-[10px] font-black italic", (pos.unrealizedPnl ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600')}>
                        {(pos.unrealizedPnl ?? 0) >= 0 ? '+' : ''}${(pos.unrealizedPnl ?? 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-[10px] font-bold text-[var(--muted-text)]">{((pos.weight ?? 0) * 100).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </motion.div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
//  MAIN MODULE
// ═══════════════════════════════════════════════════════════════════════

export default function SimulationLabModule() {
  const [activeTab, setActiveTab] = useState<SimulationTab>('live-accounts');
  const [sharedSelectedTrade, setSharedSelectedTrade] = useState<TradeLog | null>(null);
  const sim = useSimulationStore();
  const { accounts: sharedAccounts, loading: accountsLoading, error: accountsError, refetch } = useSimulationSharedAccounts();

  // Phase 6: Cancel active simulation on unmount (page leave)
  useEffect(() => {
    return () => {
      cancelActiveSimulation();
    };
  }, []);

  const renderContent = () => {
    if (accountsLoading) {
      return (
        <div className="flex items-center justify-center py-20" data-testid="sim-loading">
          <Loader2 size={24} className="text-emerald-600 animate-spin" />
          <span className="ml-3 text-sm font-bold text-[var(--muted-text)] uppercase italic">Loading shared accounts...</span>
        </div>
      );
    }
    if (accountsError) {
      return (
        <div className="flex flex-col items-center justify-center py-20 gap-4" data-testid="sim-error">
          <XCircle size={24} className="text-rose-600" />
          <span className="text-sm font-bold text-rose-600 uppercase italic">Failed to load accounts</span>
          <button onClick={refetch} className="px-4 py-2 rounded-lg text-[10px] font-black uppercase italic bg-emerald-600 text-white hover:bg-emerald-500 transition-all">Retry</button>
        </div>
      );
    }
    switch (activeTab) {
      case 'live-accounts':
        return <ErrorBoundary fallbackTitle="Paper / Simulation accounts panel hit an error"><LiveAccountsPage accounts={sharedAccounts} onReplay={(trade) => {
          setSharedSelectedTrade(trade);
          setActiveTab('trade-replay');
        }} /></ErrorBoundary>;
      case 'trade-replay':
        return <ErrorBoundary fallbackTitle="Trade Replay panel hit an error"><TradeReplayPage initialTrade={sharedSelectedTrade} /></ErrorBoundary>;
      case 'stress-testing':
        return <ErrorBoundary fallbackTitle="Stress Testing panel hit an error"><StressTestingPage accounts={sharedAccounts} /></ErrorBoundary>;
      default:
        return null;
    }
  };

  return (
    <div className="app-container space-y-8 pb-20" data-testid="simulation-lab-root">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-[var(--app-text)] tracking-tight uppercase italic font-display">Simulation Lab</h1>
          <p className="text-[var(--muted-text)] mt-1 font-medium italic">Track real-time AI performance, replay historical trades, and stress-test institutional strategies.</p>
        </div>
        <div className="flex bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl p-1 shadow-sm">
          {[
            { id: 'live-accounts', label: 'Paper / Simulation', icon: Activity },
            { id: 'trade-replay', label: 'Trade Replay', icon: PlayCircle },
            { id: 'stress-testing', label: 'Stress Testing', icon: AlertTriangle },
          ].map((tab) => (
            <button
              key={tab.id}
              data-testid={`simulation-tab-${tab.id === 'live-accounts' ? 'live' : tab.id === 'trade-replay' ? 'replay' : 'stress'}`}
              onClick={() => {
                if (activeTab === tab.id) return;
                setActiveTab(tab.id as SimulationTab);
              }}
              className={cn(
                "px-4 py-2 rounded-lg text-[10px] font-black uppercase italic transition-all flex items-center gap-2 border",
                activeTab === tab.id
                  ? "bg-[var(--app-text)] text-[var(--card-bg)] shadow-[0_0_10px_rgba(var(--app-text-rgb),0.2)] border-[var(--app-text)]"
                  : "text-[var(--muted-text)] hover:text-[var(--app-text)] hover:bg-[var(--input-border)] border-transparent"
              )}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Global Status Banner */}
      <SimulationStatusBanner />

      {/* Account Supervision Panel (paper/simulation diagnostic) */}
      <AccountSupervisionPanel />

      {/* CTA — Start Trading */}
      <div className="flex items-center justify-between p-6 bg-gradient-to-r from-emerald-500/10 via-sky-500/10 to-indigo-500/10 border border-emerald-500/20 rounded-3xl" data-testid="sim-cta-banner">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-600/20">
            <Shield size={24} className="text-white" />
          </div>
          <div>
            <div className="text-sm font-black text-[var(--app-text)] uppercase italic">Watch AI Trade Live — Or Start Your Own</div>
            <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">These accounts are managed by the AI Council. Create your own to trade with AI guidance.</div>
          </div>
        </div>
        <a
          href="/register"
          data-testid="sim-cta-register"
          className="px-6 py-3 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase italic hover:bg-emerald-500 transition-all shadow-lg shadow-emerald-600/20 flex items-center gap-2 whitespace-nowrap"
        >
          Start Trading Your Own Account <ArrowRight size={14} />
        </a>
      </div>

      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {renderContent()}
      </motion.div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  PAGE 1: LIVE ACCOUNTS
// ═══════════════════════════════════════════════════════════════════════

function LiveAccountsPage({ accounts, onReplay }: { accounts: SimulationAccount[]; onReplay: (trade: TradeLog) => void }) {
  const detailRef = useRef<HTMLDivElement>(null);
  // Bullet-proofed initial value: ensure required numeric fields exist as nulls so descendants
  // can use ?? guards without hitting undefined.toFixed during the first render frame.
  const _safeInitial = (() => {
    const raw = accounts[accounts.length - 1] ?? ({} as SimulationAccount);
    return {
      ...raw,
      tradeCount: (raw as any).tradeCount ?? 0,
      activeDuration: (raw as any).activeDuration ?? '',
      roi: (raw as any).roi ?? 0,
      winRate: (raw as any).winRate ?? null,
      consistencyScore: (raw as any).consistencyScore ?? 0,
      balance: (raw as any).balance ?? null,
      maxDrawdown: (raw as any).maxDrawdown ?? null,
      style: (raw as any).style ?? '',
      id: (raw as any).id ?? '',
    } as SimulationAccount;
  })();
  const [selectedAccount, setSelectedAccount] = useState<SimulationAccount>(_safeInitial);
  const [selectedTrade, setSelectedTrade] = useState<TradeLog | null>(null);

  // Sync selectedAccount when accounts load or change — prevents stale empty
  // object from initial render when accounts were still loading.
  useEffect(() => {
    if (accounts.length > 0 && !selectedAccount?.id) {
      setSelectedAccount(accounts[accounts.length - 1]);
    }
  }, [accounts]);
  const [isLive, setIsLive] = useState(true);
  const [leaderboardSort, setLeaderboardSort] = useState<'ROI %' | 'Win Rate' | 'Consistency'>('ROI %');
  const [tradeFilter, setTradeFilter] = useState<'All' | 'High Confidence' | 'High Agreement' | 'Losing Trades'>('All');
  const [showCorrelation, setShowCorrelation] = useState(false);
  const [equityRange, setEquityRange] = useState<'1D' | '1W' | '1M' | '3M' | 'ALL'>('ALL');
  const [decisionsScope, setDecisionsScope] = useState<'today' | 'all'>('today');
  const [selectedDecision, setSelectedDecision] = useState<BrainDecision | null>(null);
  const [expandedDecisionId, setExpandedDecisionId] = useState<string | null>(null);
  const [showDebate, setShowDebate] = useState(false);
  const [decisionPage, setDecisionPage] = useState(0);
  const [tradePage, setTradePage] = useState(0);
  const ROWS_PER_PAGE = 20;

  const live = useAccountLiveData(selectedAccount?.id, equityRange);

  // Phase 11 — only the OANDA forex paper account uses the forex bridge.
  // The hook short-circuits when disabled to avoid polling for hidden data.
  const isForexAccount = selectedAccount?.accountType === 'forex_paper';
  const forex = useForexPaperAccount(isForexAccount);

  // Phase 2 — Reset stale state on account change
  useEffect(() => {
    setSelectedTrade(null);
    setSelectedDecision(null);
    setExpandedDecisionId(null);
    setShowDebate(false);
    setDecisionPage(0);
    setTradePage(0);
  }, [selectedAccount?.id]);

  const sim = useSimulationStore();

  // Run simulation for the selected account
  const handleRunSimulation = useCallback(async () => {
    // Null-safety guard: the sim engine requires a concrete starting
    // capital. If the selected account hasn't finished loading yet
    // (balance still null), do nothing -- silently swallowing here is
    // safer than passing null through and letting the engine reject.
    if (selectedAccount.balance === null || selectedAccount.balance === undefined) return;
    logInteraction('LiveAccounts', 'Run Simulation', { accountId: selectedAccount.id });
    try {
      await sim.executeRunSimulation({
        accountId: selectedAccount.id,
        strategy: selectedAccount.style,
        initialCapital: selectedAccount.balance,
        duration: selectedAccount.activeDuration,
        // consistencyScore can be null per the SimulationAccount type; treat
        // a missing score as the "no-deviation" baseline (0) for risk math.
        riskTolerance: Math.round((100 - safeMetricValue(selectedAccount.consistencyScore, 0)) / 10),
        symbols: ['NVDA', 'AAPL', 'TSLA', 'AMD', 'MSFT'],
      });
    } catch {
      // Error handled by store
    }
  }, [selectedAccount, sim]);

  const sortedAccounts = useMemo(() => {
    return [...accounts].sort((a, b) => {
      if (leaderboardSort === 'ROI %') return sortKeyOrPushDown(b.roi) - sortKeyOrPushDown(a.roi);
      if (leaderboardSort === 'Win Rate') return sortKeyOrPushDown(b.winRate) - sortKeyOrPushDown(a.winRate);
      if (leaderboardSort === 'Consistency') return b.consistencyScore - a.consistencyScore;
      return 0;
    });
  }, [accounts, leaderboardSort]);

  const filteredTrades = useMemo(() => {
    // Phase 6 — use real broker trades from live hook, NOT mock data
    const realTrades = (live.trades ?? []).map(t => ({
      id: t.trade_id,
      asset: t.symbol,
      action: t.side?.toUpperCase() || '—',
      type: (t.side?.toUpperCase() === 'SELL' ? 'EXIT' : 'ENTRY') as 'ENTRY' | 'EXIT',
      time: t.executed_at || t.created_at || '—',
      confidence: t.decision_confidence ?? 0,
      agreement: 0,
      pnl: null as number | null,
      oneLineReasoning: t.decision_summary || '—',
      context: '',
      deliberation: { claude: [] as string[], gpt: [] as string[], gemini: [] as string[], perplexity: [] as string[] },
      signals: [] as string[],
      outcome: { success: true, pnl: 0, failure: null as string | null },
      lessons: [] as string[],
      accountId: t.account_id,
      // Extra fields for display
      _status: t.status,
      _tier: t.decision_tier,
      _price: t.price,
      _quantity: t.quantity,
    }));
    return realTrades.filter(trade => {
      if (tradeFilter === 'High Confidence') return trade.confidence > 80;
      if (tradeFilter === 'High Agreement') return trade.agreement > 75;
      if (tradeFilter === 'Losing Trades') return (trade.pnl || 0) < 0;
      return true;
    });
  }, [live.trades, tradeFilter]);

  // Hard-filtered decisions: account guard + dedup + micro L2/day + cadence + cap 25
  const MAX_VISIBLE_DECISIONS = 25;
  const filteredDecisions = useMemo(() => {
    if (!live.decisions?.length) return [];
    let decs = live.decisions.filter(d => d.account_id === selectedAccount?.id);
    if (decisionsScope === 'today') {
      const today = new Date().toISOString().slice(0, 10);
      decs = decs.filter(d => d.created_at?.slice(0, 10) === today);
    }
    decs = [...decs].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    const seen = new Map<string, string>();
    const deduped = decs.filter(d => {
      const key = d.symbol + '-' + d.action;
      const prev = seen.get(key);
      if (prev && Math.abs(new Date(d.created_at).getTime() - new Date(prev).getTime()) < 600000) return false;
      seen.set(key, d.created_at);
      return true;
    });
    let result = deduped;
    if (selectedAccount?.id === 'sim-brain-scalper') {
      const l2PerDay = new Map<string, number>();
      result = deduped.filter(d => {
        if (d.tier === 'L2') {
          const day = (d.created_at || '').slice(0, 10);
          const count = (l2PerDay.get(day) || 0) + 1;
          l2PerDay.set(day, count);
          if (count > 2) {
            // eslint-disable-next-line no-console
            console.warn('[MICRO L2 CAP] Excess L2 on ' + day + ' count=' + count);
            return false;
          }
        }
        return true;
      });
    }
    if (selectedAccount?.style === 'Swing' && decisionsScope !== 'today') {
      const perDay = new Map<string, number>();
      result = result.filter(d => {
        const day = (d.created_at || '').slice(0, 10);
        const count = (perDay.get(day) || 0) + 1;
        perDay.set(day, count);
        return count <= 5;
      });
    }
    return result.slice(0, MAX_VISIBLE_DECISIONS);
  }, [live.decisions, decisionsScope, selectedAccount?.id, selectedAccount?.style]);

  // Deterministic equity: filter, sort, cache key, hash
  const equityCacheKeyRef = useRef<string>('');
  const equityHashRef = useRef<string>('');
  const validatedEquityPoints = useMemo(() => {
    const pts = live.equity?.points ?? [];
    if (pts.length === 0) return [];
    const activation = ACCOUNT_ACTIVATION_DATES[selectedAccount?.id] || '';
    const filtered = activation ? pts.filter(p => (p.t || '').slice(0, 10) >= activation) : [...pts];
    filtered.sort((a, b) => (a.t || '').localeCompare(b.t || ''));
    const cacheKey = selectedAccount?.id + '-' + (live.equity?.range || '') + '-' + filtered.length;
    const hash = filtered.reduce((acc, p) => acc + p.equity, 0).toFixed(2);
    if (equityCacheKeyRef.current === cacheKey && equityHashRef.current !== hash) {
      // eslint-disable-next-line no-console
      console.error('[EQUITY DATA INSTABILITY DETECTED] key=' + cacheKey + ' prev=' + equityHashRef.current + ' new=' + hash);
    }
    equityCacheKeyRef.current = cacheKey;
    equityHashRef.current = hash;
    return filtered;
  }, [live.equity?.points, live.equity?.range, selectedAccount?.id]);

  // Determine which result to show: live results from store, or null
  const activeResult = sim.results ?? sim.partialResults;

  return (
    <div className="space-y-8">
      {/* Account Selection Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {accounts.map((acc) => (
          <button
            key={acc.id}
            onClick={() => {
              console.log("CARD CLICKED", acc.id, acc.name);
              setSelectedAccount(acc);
              setTimeout(() => {
                detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
            className={cn(
              "p-5 rounded-3xl border transition-all text-left relative overflow-hidden group flex flex-col justify-between h-full cursor-pointer",
              "pointer-events-auto",
              selectedAccount.id === acc.id
                ? "bg-[var(--card-bg)] border-emerald-500 shadow-xl shadow-emerald-500/10 scale-[1.02] ring-2 ring-emerald-500/30"
                : acc.readOnly
                  ? "bg-[var(--card-bg)] border-[var(--card-border)] hover:border-indigo-500/50 hover:shadow-md"
                  : "bg-[var(--card-bg)] border-[var(--card-border)] hover:border-emerald-500/50 hover:bg-[var(--input-bg)]/50 hover:shadow-md"
            )}
          >
            <div className={cn("relative z-10 space-y-4", acc.readOnly && "opacity-85")}>
              {acc.readOnly && (
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex items-center gap-1.5 px-2 py-0.5 bg-indigo-500/10 border border-indigo-500/20 rounded-lg w-fit" title="Council-managed shared account — read only" data-testid="council-managed-badge">
                    <Lock size={9} className="text-indigo-600" />
                    <span className="text-[7px] font-black text-indigo-600 uppercase tracking-widest">AI Council Managed</span>
                  </div>
                  {(() => {
                    const freshness = getDataFreshness(acc.lastSyncedAt);
                    return (
                      <div className="flex items-center gap-1 px-2 py-0.5" data-testid="data-freshness">
                        <div className={cn("w-1.5 h-1.5 rounded-full", freshness.dotColor, freshness.label === 'Live' && "animate-pulse")} />
                        <span className={cn("text-[7px] font-black uppercase tracking-widest", freshness.color)}>{freshness.label}</span>
                      </div>
                    );
                  })()}
                </div>
              )}
              <div className="flex justify-between items-start">
                <div className="flex flex-col">
                  <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest flex items-center gap-1">
                    <Activity size={10} /> {acc.strategyName || 'AI Managed'}
                  </span>
                  <span className="text-[8px] font-bold text-[var(--muted-text)] uppercase tracking-widest mt-0.5 line-clamp-1">{acc.strategyDescription || 'Live Decision Engine'}</span>
                </div>
                <div className={cn(
                  "px-2 py-1 rounded-lg text-[10px] font-black uppercase italic",
                  (acc.roi ?? 0) >= 0 ? "bg-emerald-500/10 text-emerald-600" : "bg-rose-500/10 text-rose-600"
                )}>
                  {(acc.roi ?? 0) >= 0 ? '+' : ''}{(acc.roi ?? 0).toFixed(2)}%
                </div>
              </div>

              <div>
                <div className="text-xl font-black text-[var(--app-text)] italic leading-none">${(acc.balance ?? 0).toLocaleString()}</div>
                <div className="text-[11px] font-black text-[var(--app-text)] uppercase italic mt-1.5">{acc.name}</div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">{acc.style}</div>
                  <div className="w-1 h-1 rounded-full bg-[var(--muted-text)] opacity-30" />
                  <div className="text-[9px] font-bold text-emerald-600 uppercase tracking-widest">{acc.activeDuration} Active</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-3 pt-4 border-t border-[var(--card-border)]">
                <div>
                  <div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-tighter">Start Balance</div>
                  <div className="text-[10px] font-black text-[var(--app-text)]">${(acc.initialBalance ?? 0).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-tighter">Current ROI</div>
                  <div className={`text-[10px] font-black ${(acc.roi ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{(acc.roi ?? 0) >= 0 ? '+' : ''}{(acc.roi ?? 0).toFixed(2)}%</div>
                </div>
                <div>
                  <div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-tighter">Win Rate</div>
                  <div className="text-[10px] font-black text-[var(--app-text)]">{acc.winRate != null ? `${acc.winRate.toFixed(1)}%` : '\u2014'}</div>
                </div>
                <div>
                  <div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-tighter">Max Drawdown</div>
                  <div className="text-[10px] font-black text-rose-600">{acc.maxDrawdown != null ? `${acc.maxDrawdown}%` : '\u2014'}</div>
                </div>
                <div>
                  <div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-tighter">Trade Count</div>
                  <div className="text-[10px] font-black text-[var(--app-text)]">{acc.tradeCount} Trades</div>
                </div>
                <div>
                  <div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-tighter">Start Date</div>
                  <div className="text-[10px] font-black text-[var(--app-text)]">{safeFormatTimestamp(acc.startDate, { mode: 'date', fallback: '—', invalidFallback: '—' })}</div>
                </div>
              </div>

              {/* Trust Signals */}
              <div className="flex flex-wrap gap-2 pt-3 border-t border-[var(--card-border)]" data-testid="trust-signals">
                {acc.confidenceScore > 0 && (
                  <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-600 text-[7px] font-black uppercase rounded-lg flex items-center gap-1">
                    <Shield size={8} /> {acc.confidenceScore}% Confidence
                  </span>
                )}
                {acc.daysOfHistory > 0 && (
                  <span className="px-2 py-0.5 bg-sky-500/10 text-sky-600 text-[7px] font-black uppercase rounded-lg flex items-center gap-1">
                    <Calendar size={8} /> {acc.daysOfHistory}d Active
                  </span>
                )}
                {acc.lastTradeTime && (
                  <span className="px-2 py-0.5 bg-amber-500/10 text-amber-600 text-[7px] font-black uppercase rounded-lg flex items-center gap-1">
                    <Clock size={8} /> Last Trade: {safeFormatTimestamp(acc.lastTradeTime, { mode: 'date', fallback: '—', invalidFallback: '—' })}
                  </span>
                )}
                {(acc.tradeCount ?? 0) > 0 && (
                  <span className="px-2 py-0.5 bg-indigo-500/10 text-indigo-600 text-[7px] font-black uppercase rounded-lg flex items-center gap-1">
                    <Activity size={8} /> {acc.tradeCount} Trades
                  </span>
                )}
              </div>
            </div>
            {selectedAccount.id === acc.id && (
              <motion.div
                layoutId="active-glow"
                className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 rounded-full -mr-12 -mt-12 blur-2xl"
              />
            )}
          </button>
        ))}
      </div>

      {/*
        Phase 11: when the OANDA forex paper account is selected we render
        the Forex panel and short-circuit the Alpaca-specific dashboard
        sections below. The selector grid above stays visible so the user
        can switch back to an Alpaca account.

        IMPORTANT: this branch must NEVER render a Run Simulation button or
        any control that would attempt to trigger a live order. The
        ForexPanel is read-only by construction.
      */}
      {isForexAccount && (
        <div data-testid="forex-panel-host">
          <ForexPanel bundle={forex.bundle} loading={forex.loading} error={forex.error} />
        </div>
      )}

      {!isForexAccount && (
      <>
      {/* Run Simulation Button */}
      <div className="flex items-center gap-4">
        <button
          data-testid="sim-run-live"
          onClick={handleRunSimulation}
          disabled={sim.isLoading || sim.isRunning || selectedAccount.readOnly}
          title={selectedAccount.readOnly ? 'This is a shared system account' : undefined}
          className={cn(
            "px-6 py-3 rounded-xl text-[10px] font-black uppercase italic transition-all flex items-center gap-2 shadow-lg",
            selectedAccount.readOnly
              ? "bg-[var(--muted-text)] text-[var(--card-bg)] cursor-not-allowed opacity-40"
              : (sim.isLoading || sim.isRunning)
                ? "bg-[var(--muted-text)] text-[var(--card-bg)] cursor-not-allowed opacity-50"
                : "bg-emerald-600 text-white hover:bg-emerald-500 active:scale-95 shadow-emerald-600/20"
          )}
        >
          {selectedAccount.readOnly ? <Lock size={14} /> : <Play size={14} className="fill-white" />}
          {selectedAccount.readOnly ? 'AI Council Managed — View Only' : sim.isLoading || sim.isRunning ? 'Running...' : `Simulate ${selectedAccount.name}`}
        </button>
        {(sim.isLoading || sim.isRunning) && (
          <span className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest animate-pulse">
            Processing simulation...
          </span>
        )}
      </div>

      {/* Simulation Results (from store) */}
      {activeResult && sim.simulationType === 'live' && (
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-8 shadow-sm">
          <SimulationResultPanel result={activeResult} label="Live Simulation Results" />
        </div>
      )}

      {/* Account Dashboard */}
      <div ref={detailRef} className="scroll-mt-4">
        {/* Selected Account Header */}
        <motion.div
          key={selectedAccount.id}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between p-5 mb-6 bg-gradient-to-r from-emerald-500/5 via-transparent to-emerald-500/5 border border-emerald-500/20 rounded-2xl"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-600/20">
              <Activity size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-sm font-black text-[var(--app-text)] uppercase italic">{selectedAccount.name || 'Select an Account'}</h2>
              <div className="flex items-center gap-3">
                <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest">{selectedAccount.style || '—'}</span>
                {selectedAccount.strategyName && (
                  <>
                    <span className="w-1 h-1 rounded-full bg-[var(--muted-text)] opacity-30" />
                    <span className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">{selectedAccount.strategyName}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-tighter">Balance</div>
              <div className="text-lg font-black text-[var(--app-text)] italic">${(selectedAccount.balance ?? selectedAccount.currentBalance ?? 0).toLocaleString()}</div>
            </div>
            <div className="text-right">
              <div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-tighter">ROI</div>
              <div className={`text-sm font-black italic ${(selectedAccount.roi ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {(selectedAccount.roi ?? 0) >= 0 ? '+' : ''}{(selectedAccount.roi ?? 0).toFixed(2)}%
              </div>
            </div>
            <div className="text-right">
              <div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-tighter">Trades</div>
              <div className="text-sm font-black text-[var(--app-text)] italic">{selectedAccount.tradeCount ?? 0}</div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-8">
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-8 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
              <div className="flex items-center gap-6">
                <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic flex items-center gap-2">
                  <LineChartIcon size={20} className="text-emerald-600" /> Equity Curve
                  <SourceLabel source="Alpaca Broker" />
                </h3>
                <div className="flex bg-[var(--input-bg)] rounded-xl p-1 border border-[var(--card-border)]">
                  {([
                    { label: '7D', value: '1W' as const },
                    { label: '30D', value: '1M' as const },
                    { label: '90D', value: '3M' as const },
                    { label: 'All', value: 'ALL' as const },
                  ]).map(({ label, value }) => (
                    <button
                      key={value}
                      onClick={() => setEquityRange(value)}
                      className={cn(
                        "px-3 py-1.5 rounded-lg text-[9px] font-black uppercase italic transition-all",
                        equityRange === value ? "bg-[var(--card-bg)] text-emerald-600 shadow-sm" : "text-[var(--muted-text)] hover:text-[var(--app-text)]"
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Trade Freq</div>
                  <div className="text-xs font-black text-[var(--app-text)] italic">{((selectedAccount.tradeCount ?? 0) / (parseInt(selectedAccount.activeDuration) || 1)).toFixed(1)} / Day</div>
                </div>
                <div className="text-right">
                  <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Risk Exposure</div>
                  <div className="text-xs font-black text-amber-500 italic">18.5%</div>
                </div>
              </div>
            </div>

            <div className="h-[350px] w-full">
              {live.loading ? (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <Loader2 size={24} className="text-emerald-600 animate-spin" />
                  <p className="text-[10px] font-black text-[var(--muted-text)] uppercase italic">Loading equity data...</p>
                </div>
              ) : validatedEquityPoints.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-[10px] font-black text-[var(--muted-text)] uppercase italic">
                    No equity data available
                  </p>
                </div>
              ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={validatedEquityPoints.map(p => ({ time: p.t, value: p.equity, drawdown: 0 }))}>
                  <defs>
                    <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--card-border)" vertical={false} />
                  <XAxis dataKey="time" hide />
                  <YAxis hide domain={['auto', 'auto']} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '12px' }}
                  />
                  <Area type="monotone" dataKey="value" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorEquity)" />
                </AreaChart>
              </ResponsiveContainer>
              )}
            </div>

            {/* Debug Strip + Alignment Check */}
            <div className="flex flex-wrap items-center gap-3 px-3 py-2 mt-4 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-xl text-[8px] font-mono text-[var(--muted-text)] uppercase tracking-widest">
              <span className="inline-flex items-center gap-1.5"><SimAccountIcon id={selectedAccount.id} size={14} />Acct: <b className="text-[var(--app-text)]">{selectedAccount.id}</b></span>
              <span>Src: <b className="text-emerald-600">broker</b></span>
              <span>Pts: <b className="text-[var(--app-text)]">{live.equity?.point_count ?? 0}</b></span>
              <span>Range: <b className="text-[var(--app-text)]">{formatET(live.equity?.first_point?.t).date || '—'} → {formatET(live.equity?.last_point?.t).date || '—'}</b></span>
              <span>$: <b className="text-[var(--app-text)]">{live.equity?.first_point?.equity?.toLocaleString() ?? '—'} → {live.equity?.last_point?.equity?.toLocaleString() ?? '—'}</b></span>
              <span>Decs: <b className="text-[var(--app-text)]">{filteredDecisions.length}</b></span>
              <span>Trades: <b className="text-[var(--app-text)]">{filteredTrades.length}</b></span>
              {live.loading && <span className="text-amber-500 animate-pulse">Loading...</span>}
            </div>
            {/* Phase 6 — Strategy alignment warning */}
            {live.equity && live.equity.account_id !== selectedAccount.id && (
              <div className="flex items-center gap-2 px-3 py-2 mt-2 bg-rose-500/10 border border-rose-500/30 rounded-xl text-[9px] font-black text-rose-600 uppercase">
                <AlertTriangle size={12} /> Data mismatch: equity belongs to {live.equity.account_id}, not {selectedAccount.id}
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-[var(--card-border)]">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Drawdown Analysis</h4>
                <span className="text-[10px] font-black text-rose-600 uppercase italic">Peak DD: {selectedAccount.maxDrawdown ?? 0}%</span>
              </div>
              <div className="h-[100px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={validatedEquityPoints.map(p => ({ time: p.t, value: p.equity, drawdown: 0 }))}>
                    <Bar dataKey="drawdown" fill="#ef4444" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* AI Strategy Summary & Correlation */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-8 shadow-sm">
              <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic mb-6 flex items-center gap-2">
                <Cpu size={18} className="text-emerald-600" /> AI Strategy Summary
              </h3>
              <div className="space-y-6">
                <div>
                  <div className="text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-2">Primary Behavior</div>
                  <p className="text-xs text-[var(--app-text)] font-bold italic leading-relaxed">{selectedAccount.aiStrategySummary?.behavior ?? 'No strategy summary available'}</p>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-[9px] font-black text-emerald-600 uppercase tracking-widest mb-2">Win Conditions</div>
                    <ul className="space-y-1.5">
                      {(selectedAccount.aiStrategySummary?.winConditions ?? []).map((cond, i) => (
                        <li key={i} className="text-[10px] font-bold text-[var(--muted-text)] flex items-center gap-2">
                          <CheckCircle2 size={10} className="text-emerald-500" /> {cond}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-[9px] font-black text-rose-600 uppercase tracking-widest mb-2">Failure Patterns</div>
                    <ul className="space-y-1.5">
                      {(selectedAccount.aiStrategySummary?.failurePatterns ?? []).map((patt, i) => (
                        <li key={i} className="text-[10px] font-bold text-[var(--muted-text)] flex items-center gap-2">
                          <X size={10} className="text-rose-500" /> {patt}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-8 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic flex items-center gap-2">
                  <BarChart3 size={18} className="text-sky-600" /> Why vs Performance
                </h3>
                <button
                  onClick={() => setShowCorrelation(!showCorrelation)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg text-[9px] font-black uppercase italic transition-all border",
                    showCorrelation ? "bg-emerald-600 border-emerald-600 text-white" : "bg-[var(--input-bg)] border-[var(--card-border)] text-[var(--muted-text)]"
                  )}
                >
                  {showCorrelation ? 'Hide Correlation' : 'Show Correlation'}
                </button>
              </div>
              {showCorrelation && <div className="space-y-4">
                {(selectedAccount.reasoningCorrelation ?? []).map((corr, i) => (
                  <div key={i} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] font-black text-[var(--app-text)] uppercase italic">{corr.pattern}</span>
                      <span className={cn("text-[10px] font-black italic", (corr.roi ?? 0) >= 0 ? "text-emerald-600" : "text-rose-600")}>
                        {(corr.roi ?? 0) >= 0 ? '+' : ''}{formatNullableNumber(corr.roi, 0)}% ROI
                      </span>
                    </div>
                    <div className="w-full h-1.5 bg-[var(--input-bg)] rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${corr.winRate ?? 0}%` }}
                        className={cn("h-full", (corr.roi ?? 0) >= 0 ? "bg-emerald-500" : "bg-rose-500")}
                      />
                    </div>
                    <div className="flex justify-between text-[8px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                      <span>Win Rate: {formatNullablePercent(corr.winRate, 0)}</span>
                    </div>
                  </div>
                ))}
              </div>}
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/*  TODAY'S COUNCIL DECISIONS                                  */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl overflow-hidden shadow-sm">
            <div className="px-8 py-6 border-b border-[var(--card-border)] flex flex-col md:flex-row md:items-center justify-between gap-4">
              <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic flex items-center gap-2">
                <Brain size={18} className="text-indigo-600" /> Council Decisions
                <SourceLabel source="Decision Store" icon={<Brain size={8} />} />
              </h3>
              <div className="flex items-center gap-4">
                <div className="flex bg-[var(--input-bg)] rounded-xl p-1 border border-[var(--card-border)]">
                  {(['today', 'all'] as const).map((scope) => (
                    <button
                      key={scope}
                      onClick={() => setDecisionsScope(scope)}
                      className={cn(
                        "px-3 py-1.5 rounded-lg text-[9px] font-black uppercase italic transition-all",
                        decisionsScope === scope ? "bg-[var(--card-bg)] text-emerald-600 shadow-sm" : "text-[var(--muted-text)] hover:text-[var(--app-text)]"
                      )}
                    >
                      {scope === 'today' ? 'Today' : 'All History'}
                    </button>
                  ))}
                </div>
                <span className="text-[8px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                  {filteredDecisions.length} decision{filteredDecisions.length !== 1 ? 's' : ''}
                  {filteredDecisions.length > ROWS_PER_PAGE && <> &middot; Pg {decisionPage + 1}/{Math.ceil(filteredDecisions.length / ROWS_PER_PAGE)}</>}
                </span>
                {filteredDecisions.length > ROWS_PER_PAGE && (
                  <div className="flex gap-1">
                    <button onClick={() => setDecisionPage(p => Math.max(0, p - 1))} disabled={decisionPage === 0} className="px-2 py-1 text-[9px] font-black uppercase rounded-lg border border-[var(--card-border)] disabled:opacity-30">Prev</button>
                    <button onClick={() => setDecisionPage(p => Math.min(Math.ceil(filteredDecisions.length / ROWS_PER_PAGE) - 1, p + 1))} disabled={decisionPage >= Math.ceil(filteredDecisions.length / ROWS_PER_PAGE) - 1} className="px-2 py-1 text-[9px] font-black uppercase rounded-lg border border-[var(--card-border)] disabled:opacity-30">Next</button>
                  </div>
                )}
              </div>
            </div>
            {live.loading ? (
              <div className="px-8 py-6"><PanelSkeleton rows={4} label="Loading decisions..." /></div>
            ) : filteredDecisions.length === 0 ? (
              <div className="px-8 py-12 text-center">
                <Brain size={32} className="text-[var(--muted-text)] mx-auto mb-3 opacity-30" />
                <p className="text-[10px] font-black text-[var(--muted-text)] uppercase italic">
                  No decisions {decisionsScope === 'today' ? 'today' : 'found'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-[var(--input-bg)] text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest">
                      <th className="px-3 py-3">Time</th>
                      <th className="px-3 py-3">Symbol</th>
                      <th className="px-3 py-3">Action</th>
                      <th className="px-3 py-3">Confidence</th>
                      <th className="px-3 py-3">Tier</th>
                      <th className="px-3 py-3">Status</th>
                      <th className="px-3 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--card-border)]">
                    {filteredDecisions.slice(decisionPage * ROWS_PER_PAGE, (decisionPage + 1) * ROWS_PER_PAGE).map((d) => (
                      <React.Fragment key={d.decision_id}>
                        <motion.tr
                          whileHover={{ backgroundColor: 'var(--input-bg)' }}
                          className="transition-colors cursor-pointer"
                          onClick={() => setSelectedDecision(d)}
                        >
                          <td className="px-3 py-3">
                            <div className="flex flex-col">
                              <span className="text-[10px] font-black text-[var(--app-text)] uppercase italic">
                                {formatET(d.created_at).time}
                              </span>
                              <span className="text-[8px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                                {formatET(d.created_at).date}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <span className="text-xs font-black text-[var(--app-text)] italic">{d.symbol}</span>
                          </td>
                          <td className="px-3 py-3">
                            <span className={cn(
                              "px-2 py-0.5 rounded text-[9px] font-black uppercase",
                              d.action?.toUpperCase() === 'BUY' ? "bg-emerald-500/10 text-emerald-600" : "bg-rose-500/10 text-rose-600"
                            )}>
                              {d.action}
                            </span>
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-12 h-1 bg-[var(--input-bg)] rounded-full overflow-hidden">
                                <motion.div initial={{ width: 0 }} animate={{ width: `${(d.confidence * 100)}%` }} className={cn("h-full", d.confidence >= 0.7 ? "bg-emerald-500" : d.confidence >= 0.4 ? "bg-amber-500" : "bg-rose-500")} />
                              </div>
                              <span className="text-[10px] font-black text-[var(--app-text)]">{Math.round(d.confidence * 100)}%</span>
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <span className={cn(
                              "inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-black uppercase",
                              d.tier === 'L2' ? "bg-indigo-500/10 text-indigo-600" :
                              d.tier === 'L1' ? "bg-amber-500/10 text-amber-600" :
                              "bg-gray-500/10 text-[var(--muted-text)]"
                            )}>
                              {d.tier === 'L2' ? <Brain size={10} /> : d.tier === 'L1' ? <Zap size={10} /> : <Activity size={10} />}
                              {d.tier}
                            </span>
                          </td>
                          <td className="px-3 py-3">
                            <span className={cn(
                              "px-2 py-0.5 rounded text-[9px] font-black uppercase",
                              d.outcome === 'executed' ? "bg-emerald-500/10 text-emerald-600" :
                              d.outcome === 'blocked' ? "bg-amber-500/10 text-amber-600" :
                              d.outcome === 'system_issue' ? "bg-rose-500/10 text-rose-600" :
                              d.outcome === 'skipped' ? "bg-gray-500/10 text-[var(--muted-text)]" :
                              "bg-gray-500/10 text-[var(--muted-text)]"
                            )}>
                              {d.outcome === 'system_issue' ? 'system issue' : d.outcome}
                            </span>
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              {d.tier === 'L2' && d.debate && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setExpandedDecisionId(expandedDecisionId === d.decision_id ? null : d.decision_id); }}
                                  className="p-1 hover:bg-[var(--input-bg)] rounded-lg transition-colors text-[var(--muted-text)]"
                                  title="Toggle debate details"
                                >
                                  {expandedDecisionId === d.decision_id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                </button>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); setSelectedDecision(d); }}
                                className="p-1 hover:bg-[var(--input-bg)] rounded-lg transition-colors text-[var(--muted-text)]"
                                title="View full decision"
                              >
                                <Eye size={14} />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                        {/* Summary + key factors collapsed preview */}
                        {(d.summary || (d as any).key_factors?.length > 0) && (
                          <tr className="border-0">
                            <td colSpan={7} className="px-8 pb-3 pt-0">
                              {d.summary && (
                                <div className="text-[10px] text-[var(--muted-text)] italic line-clamp-1">{cleanMarkdown(d.summary)}</div>
                              )}
                              {(d as any).key_factors?.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                  {(d as any).key_factors.slice(0, 2).map((f: string, i: number) => (
                                    <span key={i} className="px-2 py-0.5 bg-emerald-500/8 text-emerald-600 text-[8px] font-bold uppercase rounded-full border border-emerald-500/15">
                                      {f}
                                    </span>
                                  ))}
                                  {(d as any).key_factors.length > 2 && (
                                    <span className="text-[8px] text-[var(--muted-text)] font-bold">+{(d as any).key_factors.length - 2} more</span>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                        {/* Expandable L2 debate row */}
                        <AnimatePresence>
                          {expandedDecisionId === d.decision_id && d.tier === 'L2' && d.debate && (
                            <motion.tr
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                            >
                              <td colSpan={7} className="px-8 py-4 bg-[var(--input-bg)]">
                                {/* Key Factors */}
                                {(d as any).key_factors?.length > 0 && (
                                  <div className="mb-4">
                                    <div className="text-[8px] font-black text-emerald-600 uppercase tracking-widest mb-1">Key Factors</div>
                                    {(d as any).key_factors.map((f: string, i: number) => (
                                      <div key={i} className="text-[10px] text-[var(--app-text)] font-medium flex items-center gap-1.5 mt-0.5">
                                        <div className="w-1 h-1 rounded-full bg-emerald-500" />
                                        {f}
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Risks */}
                                {(d as any).risks?.length > 0 && (
                                  <div className="mb-4">
                                    <div className="text-[8px] font-black text-rose-600 uppercase tracking-widest mb-1">Risks</div>
                                    {(d as any).risks.map((r: string, i: number) => (
                                      <div key={i} className="text-[10px] text-[var(--app-text)] font-medium flex items-center gap-1.5 mt-0.5">
                                        <div className="w-1 h-1 rounded-full bg-rose-500" />
                                        {r}
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  {d.debate.bull && (
                                    <div className="p-4 bg-[var(--card-bg)] border border-emerald-500/20 rounded-2xl">
                                      <div className="text-[8px] font-black text-emerald-600 uppercase tracking-widest mb-2 flex items-center gap-1">
                                        <TrendingUp size={10} /> Bull Case
                                      </div>
                                      <p className="text-[10px] text-[var(--app-text)] font-medium italic leading-relaxed">{cleanMarkdown(d.debate.bull)}</p>
                                    </div>
                                  )}
                                  {d.debate.bear && (
                                    <div className="p-4 bg-[var(--card-bg)] border border-rose-500/20 rounded-2xl">
                                      <div className="text-[8px] font-black text-rose-600 uppercase tracking-widest mb-2 flex items-center gap-1">
                                        <TrendingDown size={10} /> Bear Case
                                      </div>
                                      <p className="text-[10px] text-[var(--app-text)] font-medium italic leading-relaxed">{cleanMarkdown(d.debate.bear)}</p>
                                    </div>
                                  )}
                                  {d.debate.moderator && (
                                    <div className="p-4 bg-[var(--card-bg)] border border-indigo-500/20 rounded-2xl">
                                      <div className="text-[8px] font-black text-indigo-600 uppercase tracking-widest mb-2 flex items-center gap-1">
                                        <Brain size={10} /> Moderator Summary
                                      </div>
                                      <p className="text-[10px] text-[var(--app-text)] font-medium italic leading-relaxed">{cleanMarkdown(d.debate.moderator)}</p>
                                    </div>
                                  )}
                                </div>
                                {d.debate.disagreements && d.debate.disagreements.length > 0 && (
                                  <div className="mt-4 p-4 bg-[var(--card-bg)] border border-amber-500/20 rounded-2xl">
                                    <div className="text-[8px] font-black text-amber-600 uppercase tracking-widest mb-2 flex items-center gap-1">
                                      <AlertTriangle size={10} /> Disagreements
                                    </div>
                                    <ul className="space-y-1">
                                      {d.debate.disagreements.map((dis, i) => (
                                        <li key={i} className="text-[10px] text-[var(--app-text)] font-medium italic flex items-center gap-2">
                                          <div className="w-1 h-1 rounded-full bg-amber-500 shrink-0" />
                                          {dis}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </td>
                            </motion.tr>
                          )}
                        </AnimatePresence>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Trade History Table */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl overflow-hidden shadow-sm">
            <div className="px-8 py-6 border-b border-[var(--card-border)] flex flex-col md:flex-row md:items-center justify-between gap-6">
              <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic flex items-center gap-2">
                <History size={18} className="text-emerald-600" /> Trade Execution History
                <SourceLabel source="Broker Executions" />
              </h3>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex bg-[var(--input-bg)] rounded-xl p-1 border border-[var(--card-border)]">
                  {['All', 'High Confidence', 'High Agreement', 'Losing Trades'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setTradeFilter(filter as any)}
                      className={cn(
                        "px-3 py-1.5 rounded-lg text-[9px] font-black uppercase italic transition-all",
                        tradeFilter === filter ? "bg-[var(--card-bg)] text-emerald-600 shadow-sm" : "text-[var(--muted-text)] hover:text-[var(--app-text)]"
                      )}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-xl">
                  <Search size={14} className="text-[var(--muted-text)]" />
                  <input
                    type="text"
                    placeholder="Filter by asset..."
                    className="bg-transparent border-none outline-none text-[10px] font-bold text-[var(--app-text)] uppercase w-32"
                  />
                </div>
                <span className="text-[8px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                  {filteredTrades.length} trade{filteredTrades.length !== 1 ? 's' : ''}
                  {filteredTrades.length > ROWS_PER_PAGE && <> &middot; Pg {tradePage + 1}/{Math.ceil(filteredTrades.length / ROWS_PER_PAGE)}</>}
                </span>
                {filteredTrades.length > ROWS_PER_PAGE && (
                  <div className="flex gap-1">
                    <button onClick={() => setTradePage(p => Math.max(0, p - 1))} disabled={tradePage === 0} className="px-2 py-1 text-[9px] font-black uppercase rounded-lg border border-[var(--card-border)] disabled:opacity-30">Prev</button>
                    <button onClick={() => setTradePage(p => Math.min(Math.ceil(filteredTrades.length / ROWS_PER_PAGE) - 1, p + 1))} disabled={tradePage >= Math.ceil(filteredTrades.length / ROWS_PER_PAGE) - 1} className="px-2 py-1 text-[9px] font-black uppercase rounded-lg border border-[var(--card-border)] disabled:opacity-30">Next</button>
                  </div>
                )}
              </div>
            </div>
            {live.loading ? (
              <div className="px-8 py-6"><PanelSkeleton rows={5} label="Loading trades..." /></div>
            ) : filteredTrades.length === 0 ? (
              <div className="px-8 py-12 text-center">
                <History size={32} className="text-[var(--muted-text)] mx-auto mb-3 opacity-30" />
                <p className="text-[10px] font-black text-[var(--muted-text)] uppercase italic">No trades found</p>
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-[var(--input-bg)] text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest">
                    <th className="px-3 py-3">Timestamp</th>
                    <th className="px-3 py-3">Asset</th>
                    <th className="px-3 py-3">Action</th>
                    <th className="px-3 py-3">Tier</th>
                    <th className="px-3 py-3">Entry/Exit</th>
                    <th className="px-3 py-3">P&L</th>
                    <th className="px-3 py-3">Confidence</th>
                    <th className="px-3 py-3">Agreement</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--card-border)]">
                  {filteredTrades.slice(tradePage * ROWS_PER_PAGE, (tradePage + 1) * ROWS_PER_PAGE).map((trade) => (
                    <motion.tr
                      key={trade.id}
                      whileHover={{ backgroundColor: 'var(--input-bg)' }}
                      onClick={() => setSelectedTrade(trade)}
                      className="transition-colors cursor-pointer group relative"
                    >
                      <td className="px-3 py-3">
                        <div className="flex flex-col">
                          <span className="text-[10px] font-black text-[var(--app-text)] uppercase italic">
                            {formatET(trade.time).time}
                          </span>
                          <span className="text-[8px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                            {formatET(trade.time).date}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-black text-[var(--app-text)] italic">{trade.asset}</span>
                          <div className="flex gap-1">
                            <span className="px-1.5 py-0.5 bg-emerald-500/10 text-emerald-600 text-[7px] font-black uppercase rounded">AI Verified</span>
                            {trade.confidence > 80 && (
                              <span className="px-1.5 py-0.5 bg-sky-500/10 text-sky-600 text-[7px] font-black uppercase rounded">High Confidence</span>
                            )}
                          </div>
                        </div>
                        {trade.oneLineReasoning && (
                          <div className="text-[9px] text-[var(--muted-text)] italic mt-0.5 line-clamp-1">{trade.oneLineReasoning}</div>
                        )}
                        <div className="absolute left-1/2 bottom-full mb-2 -translate-x-1/2 w-48 p-3 bg-[var(--app-text)] text-[var(--card-bg)] rounded-xl opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50 shadow-2xl">
                          <div className="flex justify-between mb-1">
                            <span className="text-[8px] font-black uppercase">Confidence: {trade.confidence}%</span>
                            <span className="text-[8px] font-black uppercase">Agreement: {trade.agreement}%</span>
                          </div>
                          <p className="text-[9px] font-bold italic">"{trade.oneLineReasoning}"</p>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={cn(
                          "px-2 py-0.5 rounded text-[9px] font-black uppercase",
                          trade.action === 'BUY' ? "bg-emerald-500/10 text-emerald-600" : "bg-rose-500/10 text-rose-600"
                        )}>
                          {trade.action}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        {(() => {
                          const tier = (trade as any)._tier;
                          if (!tier) return <span className="text-[9px] text-[var(--muted-text)]">--</span>;
                          return (
                            <span className={cn(
                              "inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-black uppercase",
                              tier === 'L2' ? "bg-indigo-500/10 text-indigo-600" :
                              tier === 'L1' ? "bg-amber-500/10 text-amber-600" :
                              "bg-gray-500/10 text-[var(--muted-text)]"
                            )}>
                              {tier === 'L2' ? <Brain size={10} /> : tier === 'L1' ? <Zap size={10} /> : <Activity size={10} />}
                              {tier}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-3">
                        <span className={cn(
                          "text-[9px] font-black uppercase px-2 py-0.5 rounded",
                          (trade as any)._status === 'filled' ? "bg-emerald-500/10 text-emerald-600" :
                          (trade as any)._status === 'submitted' ? "bg-amber-500/10 text-amber-600" :
                          (trade as any)._status === 'cancelled' || (trade as any)._status === 'rejected' ? "bg-rose-500/10 text-rose-600" :
                          "bg-gray-500/10 text-[var(--muted-text)]"
                        )}>
                          {(trade as any)._status || trade.type}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span className={cn(
                          "text-xs font-black italic",
                          (trade.outcome.pnl || 0) >= 0 ? "text-emerald-600" : "text-rose-600"
                        )}>
                          {trade.outcome.pnl ? `$${(trade.outcome?.pnl ?? 0).toLocaleString()}` : '—'}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-12 h-1 bg-[var(--input-bg)] rounded-full overflow-hidden">
                            <motion.div initial={{ width: 0 }} animate={{ width: `${trade.confidence}%` }} className="h-full bg-emerald-500" />
                          </div>
                          <span className="text-[10px] font-black text-[var(--app-text)]">{trade.confidence}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-[10px] font-bold text-[var(--muted-text)]">—</span>
                      </td>
                      <td className="px-3 py-3">
                        {(() => {
                          const linked = live.decisions.find(d => d.symbol === trade.asset);
                          if (!linked) return null;
                          return (
                            <button
                              onClick={(e) => { e.stopPropagation(); setSelectedDecision(linked); }}
                              className="px-2 py-1 bg-indigo-500/10 text-indigo-600 rounded-lg text-[8px] font-black uppercase italic hover:bg-indigo-500/20 transition-all flex items-center gap-1"
                              title="View linked decision"
                            >
                              <Brain size={10} /> Decision
                            </button>
                          );
                        })()}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-4 space-y-8">
          {/* Leaderboard */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-8 shadow-sm">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic flex items-center gap-2">
                <Trophy size={18} className="text-amber-500" /> Leaderboard
              </h3>
              <div className="flex flex-col items-end gap-2">
                <div className="flex bg-[var(--input-bg)] p-1 rounded-xl border border-[var(--card-border)]">
                  {['ROI %', 'Win Rate', 'Consistency'].map((sort) => (
                    <button
                      key={sort}
                      onClick={() => setLeaderboardSort(sort as any)}
                      className={cn(
                        "px-3 py-1.5 rounded-lg text-[9px] font-black uppercase italic transition-all",
                        leaderboardSort === sort ? "bg-[var(--card-bg)] text-emerald-600 shadow-sm" : "text-[var(--muted-text)] hover:text-[var(--app-text)]"
                      )}
                    >
                      {sort}
                    </button>
                  ))}
                </div>
                <span className="text-[8px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Global Ranking System</span>
              </div>
            </div>
            <div className="space-y-4">
              <AnimatePresence mode="popLayout">
                {sortedAccounts.map((acc, i) => (
                  <motion.div
                    key={acc.id}
                    layout
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    onClick={() => {
                      console.log("LEADERBOARD CLICKED", acc.id, acc.name);
                      setSelectedAccount(acc);
                      setTimeout(() => {
                        detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                    className={cn(
                      "flex items-center justify-between p-4 rounded-2xl border transition-all cursor-pointer",
                      selectedAccount.id === acc.id
                        ? "bg-emerald-500/5 border-emerald-500/30 ring-1 ring-emerald-500/20"
                        : "bg-[var(--input-bg)] border-[var(--card-border)] hover:border-emerald-500/30 hover:bg-[var(--input-bg)]/80"
                    )}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-xl bg-[var(--card-bg)] border border-[var(--card-border)] flex items-center justify-center text-xs font-black text-[var(--app-text)] italic">
                        #{i + 1}
                      </div>
                      <div>
                        <div className="text-[11px] font-black text-[var(--app-text)] uppercase italic">{acc.strategyName || acc.name}</div>
                        <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">{acc.style}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-black text-emerald-600 italic">
                        {leaderboardSort === 'ROI %' && `${(acc.roi ?? 0) >= 0 ? '+' : ''}${(acc.roi ?? 0).toFixed(2)}%`}
                        {leaderboardSort === 'Win Rate' && `${acc.winRate != null ? acc.winRate.toFixed(1) : '\u2014'}%`}
                        {leaderboardSort === 'Consistency' && `${acc.consistencyScore}/100`}
                      </div>
                      <div className="text-[8px] font-bold text-[var(--muted-text)] uppercase tracking-widest">{leaderboardSort}</div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          {/* Active Positions — from store results or static */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-8 shadow-sm">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic flex items-center gap-2">
                <Target size={18} className="text-sky-500" /> Active Positions
              </h3>
              <div className="relative group/consistency">
                <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-500/10 text-emerald-600 rounded-lg text-[9px] font-black uppercase italic cursor-help">
                  <ShieldCheck size={12} /> Consistency: {selectedAccount.consistencyScore ?? 0}%
                </div>
              </div>
            </div>
            <div className="space-y-4">
              {((sim.results?.positions ?? []).length > 0 ? sim.results!.positions.slice(0, 3) : [
                { symbol: 'NVDA', quantity: 100, currentPrice: 894.30, unrealizedPnl: 1240.50, entryPrice: 882.0, side: 'BUY' as const, unrealizedPnlPct: 0, weight: 0, openedAt: '' },
                { symbol: 'AMD', quantity: 250, currentPrice: 162.40, unrealizedPnl: -320.20, entryPrice: 163.68, side: 'BUY' as const, unrealizedPnlPct: 0, weight: 0, openedAt: '' },
              ]).map((pos) => (
                <div key={pos.symbol} className="p-4 rounded-2xl bg-[var(--input-bg)] border border-[var(--card-border)] space-y-3">
                  <div className="flex justify-between items-center">
                    <div className="text-sm font-black text-[var(--app-text)] italic">{pos.symbol}</div>
                    <div className={cn(
                      "text-xs font-black italic",
                      (pos.unrealizedPnl ?? 0) >= 0 ? "text-emerald-600" : "text-rose-600"
                    )}>
                      {(pos.unrealizedPnl ?? 0) >= 0 ? '+' : ''}${(pos.unrealizedPnl ?? 0).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex justify-between text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                    <span>{pos.quantity} Shares</span>
                    <span>Avg: ${(pos?.entryPrice ?? pos?.currentPrice ?? 0).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Council Blog Panel Modal — unchanged */}
      <AnimatePresence>
        {selectedTrade && (
          <TradeIntelligenceModal trade={selectedTrade} onClose={() => setSelectedTrade(null)} onReplay={onReplay} />
        )}
      </AnimatePresence>

      {/* Decision Detail Modal */}
      <AnimatePresence>
        {selectedDecision && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedDecision(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-2xl max-h-[85vh] bg-[var(--card-bg)] border border-[var(--card-border)] rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="p-6 border-b border-[var(--card-border)] flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={cn(
                    "w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg",
                    selectedDecision.tier === 'L2' ? "bg-indigo-600 shadow-indigo-600/20" :
                    selectedDecision.tier === 'L1' ? "bg-amber-600 shadow-amber-600/20" :
                    "bg-gray-600 shadow-gray-600/20"
                  )}>
                    {selectedDecision.tier === 'L2' ? <Brain size={24} className="text-white" /> :
                     selectedDecision.tier === 'L1' ? <Zap size={24} className="text-white" /> :
                     <Activity size={24} className="text-white" />}
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-[var(--app-text)] uppercase italic tracking-tight">
                      {selectedDecision.symbol} — {selectedDecision.action}
                    </h3>
                    <p className="text-[10px] text-[var(--muted-text)] font-bold uppercase tracking-[0.2em]">
                      Decision {selectedDecision.decision_id?.slice(0, 8)} | {selectedDecision.tier} | {formatET(selectedDecision.created_at).full}
                    </p>
                  </div>
                </div>
                <button onClick={() => setSelectedDecision(null)} className="p-2 hover:bg-[var(--input-bg)] rounded-xl transition-colors text-[var(--muted-text)]">
                  <X size={24} />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-8 space-y-8 scrollbar-hide">
                {/* Key metrics */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-2xl text-center">
                    <div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Confidence</div>
                    <div className="text-2xl font-black text-[var(--app-text)] italic">{Math.round(selectedDecision.confidence * 100)}%</div>
                    <div className="w-full h-1.5 bg-[var(--card-border)] rounded-full mt-2 overflow-hidden">
                      <div
                        className={cn("h-full rounded-full", selectedDecision.confidence >= 0.7 ? "bg-emerald-500" : selectedDecision.confidence >= 0.4 ? "bg-amber-500" : "bg-rose-500")}
                        style={{ width: `${selectedDecision.confidence * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="p-4 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-2xl text-center">
                    <div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Tier</div>
                    <div className={cn(
                      "text-2xl font-black italic",
                      selectedDecision.tier === 'L2' ? "text-indigo-600" : selectedDecision.tier === 'L1' ? "text-amber-600" : "text-[var(--muted-text)]"
                    )}>
                      {selectedDecision.tier}
                    </div>
                  </div>
                  <div className="p-4 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-2xl text-center">
                    <div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Status</div>
                    <div className={cn(
                      "text-sm font-black italic uppercase",
                      selectedDecision.outcome === 'executed' ? "text-emerald-600" :
                      selectedDecision.outcome === 'blocked' ? "text-amber-600" :
                      selectedDecision.outcome === 'system_issue' ? "text-rose-600" :
                      "text-[var(--muted-text)]"
                    )}>
                      {selectedDecision.outcome === 'system_issue' ? 'System Issue' : selectedDecision.outcome}
                    </div>
                  </div>
                </div>

                {/* Summary */}
                {selectedDecision.summary && (
                  <div className="p-6 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-2xl">
                    <div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-2">Decision Summary</div>
                    <p className="text-xs text-[var(--app-text)] font-medium italic leading-relaxed">{cleanMarkdown(selectedDecision.summary)}</p>
                  </div>
                )}

                {/* WHY — Key Factors */}
                {(selectedDecision as any).key_factors?.length > 0 && (
                  <div className="p-6 bg-[var(--input-bg)] border border-emerald-500/20 rounded-2xl">
                    <div className="text-[8px] font-black text-emerald-600 uppercase tracking-widest mb-3">Why</div>
                    <ul className="space-y-2">
                      {(selectedDecision as any).key_factors.map((f: string, i: number) => (
                        <li key={i} className="text-[10px] text-[var(--app-text)] font-medium flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0 mt-1" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* RISK — Risks */}
                {(selectedDecision as any).risks?.length > 0 && (
                  <div className="p-6 bg-[var(--input-bg)] border border-rose-500/20 rounded-2xl">
                    <div className="text-[8px] font-black text-rose-600 uppercase tracking-widest mb-3">Risk</div>
                    <ul className="space-y-2">
                      {(selectedDecision as any).risks.map((r: string, i: number) => (
                        <li key={i} className="text-[10px] text-[var(--app-text)] font-medium flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0 mt-1" />
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Reason (if blocked/error) */}
                {selectedDecision.reason && (
                  <div className={cn(
                    "p-4 border rounded-2xl",
                    selectedDecision.outcome === 'system_issue' ? "bg-rose-500/5 border-rose-500/20" : "bg-amber-500/5 border-amber-500/20"
                  )}>
                    <div className={cn(
                      "text-[8px] font-black uppercase tracking-widest mb-2 flex items-center gap-1",
                      selectedDecision.outcome === 'system_issue' ? "text-rose-600" : "text-amber-600"
                    )}>
                      <AlertTriangle size={10} /> Reason
                    </div>
                    <p className="text-[10px] text-[var(--app-text)] font-medium italic">{cleanMarkdown(selectedDecision.reason)}</p>
                  </div>
                )}

                {/* L2 Debate Details — behind toggle */}
                {selectedDecision.tier === 'L2' && selectedDecision.debate && (
                  <div className="space-y-4">
                    <button
                      onClick={() => setShowDebate(!showDebate)}
                      className="w-full flex items-center justify-between p-3 rounded-xl bg-[var(--input-bg)] border border-[var(--card-border)] hover:border-indigo-500/30 transition-colors"
                    >
                      <span className="text-[9px] font-black text-indigo-600 uppercase tracking-widest">View Full Debate</span>
                      <ChevronDown size={14} className={cn("text-indigo-600 transition-transform", showDebate && "rotate-180")} />
                    </button>
                    {showDebate && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {selectedDecision.debate.bull && (
                            <div className="p-5 bg-[var(--input-bg)] border border-emerald-500/20 rounded-2xl">
                              <div className="text-[8px] font-black text-emerald-600 uppercase tracking-widest mb-3 flex items-center gap-1">
                                <TrendingUp size={10} /> Bull Case
                              </div>
                              <p className="text-[10px] text-[var(--app-text)] font-medium italic leading-relaxed">{cleanMarkdown(selectedDecision.debate.bull)}</p>
                            </div>
                          )}
                          {selectedDecision.debate.bear && (
                            <div className="p-5 bg-[var(--input-bg)] border border-rose-500/20 rounded-2xl">
                              <div className="text-[8px] font-black text-rose-600 uppercase tracking-widest mb-3 flex items-center gap-1">
                                <TrendingDown size={10} /> Bear Case
                              </div>
                              <p className="text-[10px] text-[var(--app-text)] font-medium italic leading-relaxed">{cleanMarkdown(selectedDecision.debate.bear)}</p>
                            </div>
                          )}
                        </div>
                        {selectedDecision.debate.moderator && (
                          <div className="p-5 bg-[var(--input-bg)] border border-indigo-500/20 rounded-2xl">
                            <div className="text-[8px] font-black text-indigo-600 uppercase tracking-widest mb-3 flex items-center gap-1">
                              <Brain size={10} /> Moderator Summary
                            </div>
                            <p className="text-[10px] text-[var(--app-text)] font-medium italic leading-relaxed">{cleanMarkdown(selectedDecision.debate.moderator)}</p>
                          </div>
                        )}
                        {selectedDecision.debate.disagreements && selectedDecision.debate.disagreements.length > 0 && (
                          <div className="p-5 bg-[var(--input-bg)] border border-amber-500/20 rounded-2xl">
                            <div className="text-[8px] font-black text-amber-600 uppercase tracking-widest mb-3 flex items-center gap-1">
                              <AlertTriangle size={10} /> Disagreements
                            </div>
                            <ul className="space-y-2">
                              {selectedDecision.debate.disagreements.map((dis, i) => (
                                <li key={i} className="text-[10px] text-[var(--app-text)] font-medium italic flex items-start gap-2">
                                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0 mt-1" />
                                  {dis}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Linked Trade */}
                {selectedDecision.trade_id && (
                  <div className="p-5 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-2xl">
                    <div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-3 flex items-center gap-1">
                      <History size={10} /> Linked Trade
                    </div>
                    <div className="flex items-center gap-4 text-[10px] font-bold text-[var(--app-text)] uppercase tracking-widest">
                      <span>Trade ID: {selectedDecision.trade_id.slice(0, 12)}...</span>
                      <span className={cn(
                        "px-2 py-0.5 rounded font-black",
                        selectedDecision.outcome === 'executed' ? "bg-emerald-500/10 text-emerald-600" : "bg-gray-500/10 text-[var(--muted-text)]"
                      )}>
                        {selectedDecision.outcome}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  TRADE INTELLIGENCE MODAL — Extracted for cleanliness
// ═══════════════════════════════════════════════════════════════════════

function TradeIntelligenceModal({ trade, onClose, onReplay }: { trade: TradeLog; onClose: () => void; onReplay: (t: TradeLog) => void }) {
  // Phase 3+6: check if deliberation has real content
  const hasDeliberation = !!(trade.deliberation &&
    Object.values(trade.deliberation).some(arr =>
      Array.isArray(arr) && arr.some(s => typeof s === 'string' && s.length > 1 && s !== String.fromCharCode(8212))
    ));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <motion.div initial={{ opacity: 0, scale: 0.95, x: 20 }} animate={{ opacity: 1, scale: 1, x: 0 }} exit={{ opacity: 0, scale: 0.95, x: 20 }}
        className="relative w-full max-w-3xl max-h-[90vh] bg-[var(--card-bg)] border border-[var(--card-border)] rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col"
      >
        <div className="p-6 border-b border-[var(--card-border)] flex items-center justify-between bg-[var(--card-header-bg)]">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-600/20">
              <BookOpen size={24} className="text-white" />
            </div>
            <div>
              <h3 className="text-xl font-black text-[var(--app-text)] uppercase italic tracking-tight">Trade Intelligence Report</h3>
              <p className="text-[10px] text-[var(--muted-text)] font-bold uppercase tracking-[0.2em]">{trade.asset} • {formatET(trade.time).full}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => onReplay(trade)} className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase italic hover:bg-emerald-500 transition-all flex items-center gap-2 shadow-lg shadow-emerald-600/20">
              <Play size={14} className="fill-white" /> Replay Trade
            </button>
            <button onClick={onClose} className="p-2 hover:bg-[var(--input-bg)] rounded-xl transition-colors text-[var(--muted-text)]">
              <X size={24} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-8 space-y-12 scrollbar-hide">
          {hasDeliberation ? (
            <>
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-600/10 border border-emerald-600/20 rounded-full">
                  <Zap size={12} className="text-emerald-600" />
                  <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest italic">Institutional Alpha Report</span>
                </div>
                <h2 className="text-4xl font-black text-[var(--app-text)] uppercase italic leading-tight tracking-tight">
                  {trade.asset} {trade.action === 'BUY' ? 'Accumulation' : 'Distribution'}: The Institutional Thesis
                </h2>
                <div className="flex flex-wrap items-center gap-6 pt-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[var(--input-bg)] border border-[var(--card-border)] flex items-center justify-center"><Target size={20} className="text-emerald-600" /></div>
                    <div><div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-widest">Confidence</div><div className="text-sm font-black text-[var(--app-text)] italic">{trade.confidence}%</div></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[var(--input-bg)] border border-[var(--card-border)] flex items-center justify-center"><Users size={20} className="text-sky-600" /></div>
                    <div><div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-widest">Agreement</div><div className="text-sm font-black text-[var(--app-text)] italic">{trade.agreement > 0 ? trade.agreement + '%' : '—'}</div></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[var(--input-bg)] border border-[var(--card-border)] flex items-center justify-center"><Clock size={20} className="text-amber-600" /></div>
                    <div><div className="text-[8px] font-black text-[var(--muted-text)] uppercase tracking-widest">Execution</div><div className="text-sm font-black text-[var(--app-text)] italic">{formatET(trade.time).time}</div></div>
                  </div>
                </div>
              </div>
              <section className="space-y-6 relative">
                <div className="absolute -left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-emerald-600 to-transparent opacity-20" />
                <h4 className="text-sm font-black text-emerald-600 uppercase tracking-[0.2em] flex items-center gap-3"><Activity size={16} /> Why Was This Trade Taken?</h4>
                <div className="p-8 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-[2rem] relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-8 opacity-5"><Info size={120} /></div>
                  <p className="text-base text-[var(--app-text)] leading-relaxed font-medium italic relative z-10">"{trade.context}"</p>
                </div>
              </section>
              <section className="space-y-8">
                <h4 className="text-sm font-black text-emerald-600 uppercase tracking-[0.2em] flex items-center gap-3"><Users size={16} /> What Did Each AI Model Say?</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {[
                    { name: 'GPT', role: 'Intelligence', content: trade.deliberation.gpt, color: 'text-purple-600 bg-purple-600/10', icon: BrainIcon },
                    { name: 'Claude', role: 'Reasoning', content: trade.deliberation.claude, color: 'text-amber-600 bg-amber-600/10', icon: OwlIcon },
                    { name: 'Gemini', role: 'Knowledge', content: trade.deliberation.gemini, color: 'text-blue-600 bg-blue-600/10', icon: ElephantIcon },
                    { name: 'Perplexity', role: 'Discovery', content: trade.deliberation.perplexity, color: 'text-teal-600 bg-teal-600/10', icon: CompassIcon },
                  ].map((item) => (
                    <div key={item.name} className="p-6 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-3xl space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="inline-flex items-center justify-center"><item.icon size={28} /></div>
                        <div><div className={cn("text-xs font-black uppercase italic", item.color.split(' ')[0])}>{item.name}</div><div className="text-[8px] font-bold text-[var(--muted-text)] uppercase tracking-widest">{item.role}</div></div>
                      </div>
                      <div className="space-y-2">
                        {item.content.map((point, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl">
                            <div className={cn("w-1.5 h-1.5 rounded-full mt-1.5 shrink-0", item.color.split(' ')[0].replace('text-', 'bg-'))} />
                            <p className="text-[11px] text-[var(--muted-text)] leading-relaxed font-medium italic">{point}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
              <section className="space-y-6">
                <h4 className="text-sm font-black text-emerald-600 uppercase tracking-[0.2em] flex items-center gap-3"><Zap size={16} /> Critical Decision Signals</h4>
                <div className="flex flex-wrap gap-4">
                  {trade.signals.map((sig) => (
                    <div key={sig} className="px-6 py-3 bg-emerald-600/5 border border-emerald-600/20 rounded-2xl text-[11px] font-black text-emerald-600 uppercase italic flex items-center gap-2"><CheckCircle2 size={14} /> {sig}</div>
                  ))}
                </div>
              </section>
              <section className="space-y-8">
                <h4 className="text-sm font-black text-emerald-600 uppercase tracking-[0.2em] flex items-center gap-3"><Trophy size={16} /> Post-Trade Analysis & Outcome</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="p-8 bg-emerald-600/5 border border-emerald-600/20 rounded-[2.5rem] space-y-4">
                    <div className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">The Result</div>
                    <p className="text-lg text-[var(--app-text)] font-black italic leading-relaxed">{trade.outcome.success}</p>
                    {trade.outcome.failure && (<div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-2xl"><p className="text-xs text-rose-600 font-bold italic">Risk Note: {trade.outcome.failure}</p></div>)}
                  </div>
                  <div className="space-y-4">
                    <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Institutional Lessons</div>
                    <div className="space-y-3">
                      {trade.lessons.map((lesson, i) => (
                        <div key={i} className="flex items-center gap-4 p-4 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-2xl">
                          <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-xs font-black text-white italic shadow-lg shadow-emerald-600/20">{i + 1}</div>
                          <p className="text-[11px] text-[var(--muted-text)] font-medium italic">{lesson}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full space-y-4 opacity-50">
              <AlertTriangle size={48} className="text-amber-500" />
              <p className="text-sm font-black text-[var(--app-text)] uppercase italic">No AI analysis available for this trade</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  PAGE 2: TRADE REPLAY — Wired to store
// ═══════════════════════════════════════════════════════════════════════

function TradeReplayPage({ initialTrade }: { initialTrade: TradeLog | null }) {
  const [selectedTrade, setSelectedTrade] = useState<TradeLog | null>(initialTrade || MOCK_TRADE_LOGS[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const sim = useSimulationStore();

  useEffect(() => {
    if (initialTrade) {
      setSelectedTrade(initialTrade);
      setCurrentStep(0);
    }
  }, [initialTrade]);

  const handleReplay = useCallback(async () => {
    if (!selectedTrade) return;
    logInteraction('TradeReplay', 'Run Replay', { tradeId: selectedTrade.id, symbol: selectedTrade.asset });
    setIsPlaying(true);
    try {
      await sim.executeReplaySimulation({
        tradeId: selectedTrade.id,
        symbol: selectedTrade.asset,
        startDate: '2026-03-01',
        endDate: '2026-04-01',
        speed: 2,
      });
    } catch {
      // Error handled by store
    } finally {
      setIsPlaying(false);
    }
  }, [selectedTrade, sim]);

  const steps = [
    { label: 'Market Context', icon: Activity, desc: 'Analyzing macro conditions and sector rotation.' },
    { label: 'Council Discussion', icon: Users, desc: 'AI models debating strategy and risk parameters.' },
    { label: 'Decision Engine', icon: Cpu, desc: 'Final synthesis and confidence scoring.' },
    { label: 'Execution', icon: Zap, desc: 'Smart routing and order placement.' },
    { label: 'Result Analysis', icon: Trophy, desc: 'Post-trade performance review and learning.' }
  ];

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 0));

  const activeResult = sim.results ?? sim.partialResults;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-4 space-y-6">
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-6 shadow-sm">
          <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic mb-6">Select Trade to Replay</h3>
          <div className="space-y-3">
            {MOCK_TRADE_LOGS.map((trade) => (
              <button
                key={trade.id}
                onClick={() => { setSelectedTrade(trade); setCurrentStep(0); }}
                className={cn(
                  "w-full p-4 rounded-2xl border transition-all text-left",
                  selectedTrade?.id === trade.id ? "border-emerald-500 bg-emerald-500/5" : "border-[var(--card-border)] hover:border-[var(--muted-text)]"
                )}
              >
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-black text-[var(--app-text)] italic">{trade.asset}</span>
                  <span className={cn("text-[10px] font-black italic", (trade.pnl || 0) >= 0 ? "text-emerald-600" : "text-rose-600")}>
                    {trade.pnl ? `+$${trade.pnl}` : 'ENTRY'}
                  </span>
                </div>
                <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">{trade.time}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="lg:col-span-8 space-y-8">
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-[2.5rem] p-8 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-12 opacity-5"><PlayCircle size={200} /></div>
          <div className="relative z-10 space-y-12">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-600/20">
                  <Play size={28} className="text-white fill-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-black text-[var(--app-text)] uppercase italic tracking-tight">Trade Replay Simulator</h3>
                  <p className="text-xs text-[var(--muted-text)] font-bold uppercase tracking-widest">Replaying: {selectedTrade?.asset} • {selectedTrade?.time}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <button onClick={prevStep} className="p-3 bg-[var(--input-bg)] border border-[var(--card-border)] text-[var(--app-text)] rounded-xl hover:bg-[var(--card-border)] transition-all"><Rewind size={20} /></button>
                <button
                  data-testid="sim-run-replay"
                  onClick={handleReplay}
                  disabled={sim.isLoading || sim.isRunning}
                  className={cn(
                    "p-4 rounded-xl transition-all shadow-lg",
                    (sim.isLoading || sim.isRunning) ? "bg-[var(--muted-text)] cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-500 shadow-emerald-600/20"
                  )}
                >
                  {(sim.isLoading || sim.isRunning) ? <Loader2 size={28} className="text-white animate-spin" /> : isPlaying ? <PauseCircle size={28} className="text-white" /> : <PlayCircle size={28} className="text-white" />}
                </button>
                <button onClick={nextStep} className="p-3 bg-[var(--input-bg)] border border-[var(--card-border)] text-[var(--app-text)] rounded-xl hover:bg-[var(--card-border)] transition-all"><FastForward size={20} /></button>
              </div>
            </div>

            {/* Replay Steps */}
            <div className="relative">
              <div className="absolute top-1/2 left-0 right-0 h-1 bg-[var(--input-bg)] -translate-y-1/2" />
              <div className="relative flex justify-between">
                {steps.map((step, i) => (
                  <div key={i} className="flex flex-col items-center gap-3 relative z-10">
                    <button onClick={() => setCurrentStep(i)} className={cn("w-10 h-10 rounded-full flex items-center justify-center transition-all border-2", i <= currentStep ? "bg-emerald-600 border-emerald-600 text-white" : "bg-[var(--card-bg)] border-[var(--card-border)] text-[var(--muted-text)]")}>
                      <step.icon size={18} />
                    </button>
                    <span className={cn("text-[9px] font-black uppercase tracking-widest", i === currentStep ? "text-emerald-600" : "text-[var(--muted-text)]")}>{step.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Step Detail */}
            <AnimatePresence mode="wait">
              <motion.div key={currentStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                className="p-8 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-3xl space-y-4"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-emerald-600/10 rounded-lg flex items-center justify-center text-emerald-600">
                    {React.createElement(steps[currentStep].icon, { size: 18 })}
                  </div>
                  <h4 className="text-lg font-black text-[var(--app-text)] uppercase italic">{steps[currentStep].label}</h4>
                </div>
                <p className="text-sm text-[var(--muted-text)] font-medium italic leading-relaxed">{steps[currentStep].desc}</p>
                {currentStep === 1 && selectedTrade && (
                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <div className="p-4 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl">
                      <div className="flex items-center gap-2 mb-1"><BrainIcon size={18} /><div className="text-[8px] font-black text-purple-600 uppercase">GPT-4 Strategy</div></div>
                      <p className="text-[10px] text-[var(--muted-text)] italic">"{selectedTrade.deliberation.gpt}"</p>
                    </div>
                    <div className="p-4 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl">
                      <div className="flex items-center gap-2 mb-1"><ElephantIcon size={18} /><div className="text-[8px] font-black text-sky-600 uppercase">Gemini Risk</div></div>
                      <p className="text-[10px] text-[var(--muted-text)] italic">"{selectedTrade.deliberation.gemini}"</p>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Replay Results from store */}
        {activeResult && sim.simulationType === 'replay' && (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-8 shadow-sm">
            <SimulationResultPanel result={activeResult} label="Replay Results" />
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  PAGE 3: STRESS TESTING — Wired to store
// ═══════════════════════════════════════════════════════════════════════

const SCENARIO_MAP: Record<string, StressScenario> = {
  'Black Monday Replay': 'market_crash',
  'Hyper-Inflation Spike': 'rate_shock',
  'Dot-Com Bubble 2.0': 'black_swan',
  'Global Conflict Escalation': 'liquidity_crisis',
  'Custom Parameters': 'custom',
};

function StressTestingPage({ accounts }: { accounts: SimulationAccount[] }) {
  const [selectedAccount, setSelectedAccount] = useState<SimulationAccount>(accounts[accounts.length - 1] ?? {} as SimulationAccount);
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null);
  const [severity, setSeverity] = useState(7);
  const [marketDrop, setMarketDrop] = useState(25);
  const [volatilityIncrease, setVolatilityIncrease] = useState(50);
  const { setIsLoading, showToast } = useFeedback();
  const sim = useSimulationStore();

  const scenarios = [
    { id: 'crash', label: 'Black Monday Replay', impact: '-12.4%', prob: 'Low', color: 'text-rose-600 bg-rose-500/10' },
    { id: 'inflation', label: 'Hyper-Inflation Spike', impact: '-6.2%', prob: 'Medium', color: 'text-amber-600 bg-amber-500/10' },
    { id: 'tech', label: 'Dot-Com Bubble 2.0', impact: '-18.5%', prob: 'Low', color: 'text-rose-600 bg-rose-500/10' },
    { id: 'geopolitical', label: 'Global Conflict Escalation', impact: '-9.1%', prob: 'Medium', color: 'text-purple-600 bg-purple-500/10' },
  ];

  const handleRun = useCallback(async (scenarioLabel: string) => {
    logInteraction('StressTesting', 'Run Simulation', { scenario: scenarioLabel });
    setSelectedScenario(scenarioLabel);

    const apiScenario = SCENARIO_MAP[scenarioLabel] || 'custom';

    try {
      await sim.executeStressTest({
        accountId: selectedAccount.id,
        scenario: apiScenario,
        severity: scenarioLabel === 'Custom Parameters' ? Math.round(marketDrop / 10) : severity,
        duration: '5 days',
        symbols: ['AAPL', 'NVDA', 'TSLA', 'MSFT', 'GOOGL'],
        customParams: scenarioLabel === 'Custom Parameters' ? {
          marketDrop: marketDrop,
          volatilityMultiplier: volatilityIncrease / 100 + 1,
        } : undefined,
      });
      showToast(`Stress test for ${scenarioLabel} completed successfully.`, 'success');
    } catch {
      showToast('Stress test failed. Please retry.', 'error');
    }
  }, [selectedAccount, sim, severity, marketDrop, volatilityIncrease, showToast]);

  const activeResult = sim.results ?? sim.partialResults;
  const showResults = activeResult && sim.simulationType === 'stress';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-4 space-y-6">
        {/* Account Selection */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
          <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic mb-4">Select Target Account</h3>
          <div className="space-y-2">
            {accounts.map(acc => (
              <button
                key={acc.id}
                onClick={() => setSelectedAccount(acc)}
                className={cn(
                  "w-full p-3 rounded-xl border text-left transition-all",
                  selectedAccount.id === acc.id ? "border-emerald-500 bg-emerald-500/5" : "border-[var(--card-border)] hover:border-[var(--muted-text)]"
                )}
              >
                <div className="flex items-center gap-2">
                  <div className="text-[10px] font-black text-[var(--app-text)] uppercase italic">{acc.name}</div>
                  {acc.readOnly && <Ban size={9} className="text-amber-600" />}
                </div>
                <div className="text-[8px] font-bold text-[var(--muted-text)] uppercase tracking-widest">${(acc.balance ?? 0).toLocaleString()} Balance</div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
          <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic mb-6 flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-500" /> Historical Scenarios
          </h3>
          <div className="space-y-3">
            {scenarios.map((scen) => (
              <button
                key={scen.id}
                data-testid={`stress-scenario-${scen.id}`}
                disabled={sim.isLoading || sim.isRunning || selectedAccount.readOnly}
                onClick={() => handleRun(scen.label)}
                className={cn(
                  "w-full flex items-center justify-between p-4 rounded-2xl border transition-all text-left",
                  (sim.isLoading || sim.isRunning) ? "opacity-50 cursor-not-allowed" : "",
                  selectedScenario === scen.label ? "border-emerald-500 bg-emerald-500/5" : "border-[var(--card-border)] hover:border-[var(--muted-text)]"
                )}
              >
                <div className="flex items-center gap-3">
                  <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", scen.color)}>
                    <TrendingDown size={16} />
                  </div>
                  <div>
                    <div className="text-xs font-black text-[var(--app-text)] uppercase italic">{scen.label}</div>
                    <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Est. Impact: {scen.impact}</div>
                  </div>
                </div>
                <ChevronRight size={16} className="text-[var(--muted-text)]" />
              </button>
            ))}
          </div>
        </div>

        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
          <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic mb-4">Custom Stress Parameters</h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Market Drop (%)</label>
                <span className="text-[10px] font-black text-rose-600">{marketDrop}%</span>
              </div>
              <input
                type="range"
                min={5} max={60} value={marketDrop}
                onChange={(e) => setMarketDrop(Number(e.target.value))}
                className="w-full h-1.5 bg-[var(--input-bg)] rounded-full appearance-none cursor-pointer accent-emerald-500"
              />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Volatility Increase (%)</label>
                <span className="text-[10px] font-black text-amber-600">{volatilityIncrease}%</span>
              </div>
              <input
                type="range"
                min={10} max={200} value={volatilityIncrease}
                onChange={(e) => setVolatilityIncrease(Number(e.target.value))}
                className="w-full h-1.5 bg-[var(--input-bg)] rounded-full appearance-none cursor-pointer accent-emerald-500"
              />
            </div>
            <button
              data-testid="stress-btn-run-test"
              disabled={sim.isLoading || sim.isRunning || selectedAccount.readOnly}
              onClick={() => handleRun('Custom Parameters')}
              className={cn(
                "w-full py-3 rounded-xl text-[10px] font-black uppercase italic transition-all active:scale-95",
                (sim.isLoading || sim.isRunning)
                  ? "bg-[var(--muted-text)] text-[var(--card-bg)] cursor-not-allowed opacity-50"
                  : "bg-[var(--app-text)] text-[var(--card-bg)] hover:opacity-90"
              )}
            >
              {(sim.isLoading || sim.isRunning) ? 'Running...' : 'Run Custom Simulation'}
            </button>
          </div>
        </div>
      </div>

      <div className="lg:col-span-8">
        <div data-testid="stress-panel-results" className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-8 min-h-[500px] relative overflow-hidden">
          {/* Loading state */}
          {(sim.isLoading || sim.isRunning) && sim.simulationType === 'stress' ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[400px] space-y-6">
              <div className="w-20 h-20 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin mx-auto" />
              <div className="text-center">
                <h4 data-testid="sim-loading-text" className="text-xl font-black text-[var(--app-text)] uppercase italic">Simulating {selectedScenario}...</h4>
                <p className="text-sm text-[var(--muted-text)] font-medium">Analyzing {selectedAccount.name} resilience.</p>
                {sim.progress > 0 && (
                  <div className="mt-4 w-48 h-2 bg-[var(--input-bg)] rounded-full overflow-hidden mx-auto">
                    <motion.div className="h-full bg-emerald-500 rounded-full" initial={{ width: 0 }} animate={{ width: `${sim.progress}%` }} />
                  </div>
                )}
              </div>
            </div>
          ) : showResults ? (
            <SimulationResultPanel result={activeResult} label={`Stress Test: ${selectedScenario || 'Results'}`} />
          ) : (
            <div className="flex flex-col items-center justify-center h-full min-h-[400px] space-y-4 opacity-40">
              <div className="w-20 h-20 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-3xl flex items-center justify-center mx-auto">
                <Terminal size={40} className="text-[var(--muted-text)]" />
              </div>
              <div className="text-center">
                <h4 className="text-xl font-black text-[var(--app-text)] uppercase italic">Ready for Simulation</h4>
                <p className="text-sm text-[var(--muted-text)] font-medium italic">Select a scenario to analyze {selectedAccount.name} resilience.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
